/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { 
  collection, 
  getDocs, 
  setDoc, 
  doc, 
  updateDoc, 
  addDoc, 
  query, 
  where,
  deleteDoc,
  onSnapshot
} from 'firebase/firestore';
import { db } from './firebase.ts';
import { ensureValidMeetLink } from './meetUtils.ts';
import { 
  Client, 
  CostCenter, 
  Destination,
  GoodsItem, 
  Folio, 
  PreInvoice, 
  CalendarEvent, 
  MeetMeeting,
  ProcessStage,
  StageHistory,
  UserProfile,
  ActivityLog,
  DriverLocation,
  PendingAction,
  PoRecord,
  PoFolioSnapshot,
  ProjectItem,
  isClientRole,
  isAdminRole,
  ReworkIncident
} from '../types.ts';
import { getInitialProjectsForCostCenter } from '../data/initialProjectsData.ts';

// Core Seed Data Definition
const INITIAL_CLIENTS: Client[] = [
  {
    id: "CLI-MAZDA",
    code: "MAZDA",
    name: "Mazda Motor de México",
    rfc: "MMM110312AB1",
    contactEmail: "logistica@mazda.com.mx",
    contactPhone: "464-641-8000",
    address: "Carretera Salamanca-Morelia Km 3.5, Salamanca, Guanajuato",
    lat: 20.5731,
    lng: -101.1963,
    costCenterIds: ["CC-MAZDA-BAJIO"]
  },
  {
    id: "CLI-BMW",
    code: "BMW GROUP",
    name: "BMW GROUP",
    rfc: "BMW080415G32",
    contactEmail: "logistica@bmwgroup.com",
    contactPhone: "444-800-2690",
    address: "Parque Industrial Desarrollos Logísticos, San Luis Potosí, S.L.P.",
    lat: 22.1565,
    lng: -100.9855,
    costCenterIds: ["CC-BMW-SLP", "CC-BMW-PUE"]
  },
  {
    id: "CLI-RONAL",
    code: "RONAL",
    name: "Ronal Group México",
    rfc: "RGM090820KL4",
    contactEmail: "operaciones@ronalgroup.com",
    contactPhone: "442-238-1200",
    address: "Av. de las Ciencias 2000, Parque Industrial Querétaro, El Marqués, Qro.",
    lat: 20.5888,
    lng: -100.3899,
    costCenterIds: ["CC-BMW-SLP", "CC-RONAL-QRO"]
  },
  {
    id: "CLI-PROMAQ",
    code: "CLI-PROMAQ",
    name: "Procesos de Maquinado S.A. de C.V.",
    rfc: "PMA120415G32",
    contactEmail: "pedroborja91@gmail.com",
    contactPhone: "818-456-7890",
    address: "Av. de la Juventud 120, Parque Industrial Saltillo-Ramos Arizpe, Coahuila",
    lat: 25.5420,
    lng: -100.9540,
    costCenterIds: ["CC-PROMAQ-SLT", "CC-PROMAQ-MTY"]
  },
  {
    id: "CLI-METALS",
    code: "CLI-METALS",
    name: "Metalsa S.A. de C.V.",
    rfc: "MET850101TX9",
    contactEmail: "contacto@metalsa.com",
    contactPhone: "818-123-4567",
    address: "Carretera Miguel Alemán Km. 16.5, Apodaca, Nuevo León",
    lat: 25.7820,
    lng: -100.1850,
    costCenterIds: ["CC-PROMAQ-MTY"]
  }
];

export function sanitizeCostCenterCoordinates(cc: CostCenter): CostCenter {
  if (!cc) return cc;
  let lat = cc.lat;
  let lng = cc.lng;

  const codeUpper = (cc.code || '').toUpperCase();
  const nameUpper = (cc.name || '').toUpperCase();
  const addressUpper = (cc.address || '').toUpperCase();

  const isBajio = 
    cc.id === 'CC-MAZDA-BAJIO' || 
    codeUpper.includes('BAJIO') || 
    nameUpper.includes('BAJIO') || 
    nameUpper.includes('SALAMANCA') || 
    addressUpper.includes('SALAMANCA') || 
    addressUpper.includes('GUANAJUATO');

  const isQueretaro = 
    cc.id === 'CC-RONAL-QRO' || 
    codeUpper.includes('QUERETARO') || 
    codeUpper.includes('QRO') || 
    nameUpper.includes('QUERETARO') || 
    addressUpper.includes('QUERETARO');

  const isSanLuis = 
    cc.id === 'CC-BMW-SLP' || 
    codeUpper.includes('SAN LUIS') || 
    nameUpper.includes('SAN LUIS') || 
    codeUpper.includes('SLP') || 
    nameUpper.includes('SLP') || 
    addressUpper.includes('SAN LUIS');

  const isPuebla = 
    cc.id === 'CC-BMW-PUE' || 
    codeUpper.includes('PUEBLA') || 
    nameUpper.includes('PUEBLA') || 
    addressUpper.includes('PUEBLA');

  const isSaltillo = 
    cc.id === 'CC-PROMAQ-SLT' || 
    codeUpper.includes('SALTILLO') || 
    nameUpper.includes('SALTILLO') || 
    addressUpper.includes('SALTILLO');

  const isMonterrey = 
    cc.id === 'CC-PROMAQ-MTY' || 
    codeUpper.includes('MONTERREY') || 
    nameUpper.includes('MONTERREY') || 
    addressUpper.includes('NUEVO LEÓN') || 
    addressUpper.includes('NUEVO LEON');

  if (isBajio) {
    lat = 20.5731;
    lng = -101.1963;
  } else if (isQueretaro) {
    lat = 20.5888;
    lng = -100.3899;
  } else if (isSanLuis) {
    lat = 22.1565;
    lng = -100.9855;
  } else if (isPuebla) {
    lat = 19.1234;
    lng = -98.2456;
  } else if (isSaltillo) {
    lat = 25.5015;
    lng = -100.8120;
  } else if (isMonterrey) {
    lat = 25.7420;
    lng = -100.3120;
  }

  return {
    ...cc,
    lat: lat ?? 25.6866,
    lng: lng ?? -100.3161
  };
}

const INITIAL_COST_CENTERS: CostCenter[] = [
  {
    id: "CC-MAZDA-BAJIO",
    clientId: "CLI-MAZDA",
    clientIds: ["CLI-MAZDA"],
    clientName: "Mazda Motor de México",
    code: "BAJIO",
    name: "Sede Bajío - Mazda Planta Salamanca",
    manager: "Ing. Kenji Takahashi",
    address: "Carretera Salamanca-Morelia Km 3.5, Salamanca, Guanajuato",
    lat: 20.5731,
    lng: -101.1963
  },
  {
    id: "CC-BMW-SLP",
    clientId: "CLI-BMW",
    clientIds: ["CLI-BMW", "CLI-RONAL"],
    clientName: "BMW GROUP / Ronal Group México",
    code: "SAN LUIS POTOSI",
    name: "Sede San Luis Potosí - Planta BMW",
    manager: "Ing. Klaus Weber",
    address: "Bruselas 78, Parque Industrial, Logistik II, 79526 Valle de San Francisco, S.L.P.",
    lat: 22.1565,
    lng: -100.9855
  },
  {
    id: "CC-RONAL-QRO",
    clientId: "CLI-RONAL",
    clientIds: ["CLI-RONAL"],
    clientName: "Ronal Group México",
    code: "QUERETARO",
    name: "Sede Querétaro - Planta Ronal Group",
    manager: "Ing. Andrea Schmidt",
    address: "Av. de las Ciencias 2000, El Marqués, Querétaro",
    lat: 20.5888,
    lng: -100.3899
  },
  {
    id: "CC-BMW-PUE",
    clientId: "CLI-BMW",
    clientIds: ["CLI-BMW"],
    clientName: "BMW GROUP",
    code: "PUEBLA",
    name: "Sede Puebla - Centro de Distribución BMW",
    manager: "Ing. Roberto Garza",
    address: "Zona Industrial San Lorenzo Almecatla, Cuautlancingo, Puebla",
    lat: 19.1234,
    lng: -98.2456
  },
  {
    id: "CC-PROMAQ-SLT",
    clientId: "CLI-PROMAQ",
    clientIds: ["CLI-PROMAQ"],
    clientName: "Procesos de Maquinado S.A. de C.V.",
    code: "SALTILLO",
    name: "Sede Coahuila - Planta Saltillo Ensamble",
    manager: "Ing. Pedro Borja",
    address: "Bulevar Isidro López Zertuche 5120, Ramos Arizpe, Coahuila",
    lat: 25.5015,
    lng: -100.8120
  },
  {
    id: "CC-PROMAQ-MTY",
    clientId: "CLI-PROMAQ",
    clientIds: ["CLI-PROMAQ", "CLI-METALS"],
    clientName: "Procesos de Maquinado / Metalsa",
    code: "MONTERREY",
    name: "Sede Nuevo León - Planta Monterrey Maquinado",
    manager: "Ing. Carlos Mendoza",
    address: "Av. Universidad 1200, San Nicolás de los Garza, Nuevo León",
    lat: 25.7420,
    lng: -100.3120
  }
];

const INITIAL_DESTINATIONS: Destination[] = [
  {
    id: "DEST-01",
    name: "Ruta Bajío / SLP - BMW & Ronal",
    clientId: "CLI-BMW",
    clientIds: ["CLI-BMW", "CLI-RONAL"],
    clientName: "BMW GROUP / Ronal Group México",
    originName: "CEDIS Escobedo / Hub Norte",
    originAddress: "Carretera a Colombia Km. 5.5, Parque Industrial Escobedo, N.L.",
    originLat: 25.7820,
    originLng: -100.4050,
    destinationName: "BMW Planta San Luis Potosí",
    address: "Parque Industrial Desarrollos Logísticos, San Luis Potosí, S.L.P.",
    destinationAddress: "Parque Industrial Desarrollos Logísticos, San Luis Potosí, S.L.P.",
    lat: 22.1565,
    lng: -100.9855,
    routeNotes: "Autopista 57D Sur - Puerta Acceso 3 Logística",
    estimatedKm: 380,
    estimatedMins: 260,
    clientRoutes: [
      {
        clientId: "CLI-BMW",
        clientName: "BMW GROUP",
        originName: "CEDIS Escobedo / Hub Norte",
        originAddress: "Carretera a Colombia Km. 5.5, Escobedo, N.L.",
        destinationName: "Planta San Luis Potosí - Puerta 3",
        destinationAddress: "Parque Industrial Desarrollos Logísticos, San Luis Potosí, S.L.P.",
        notes: "Entrada por bahía de descarga componentes automotrices"
      },
      {
        clientId: "CLI-RONAL",
        clientName: "Ronal Group México",
        originName: "CEDIS Escobedo / Hub Norte",
        originAddress: "Carretera a Colombia Km. 5.5, Escobedo, N.L.",
        destinationName: "Nave Industrial Ronal Querétaro",
        destinationAddress: "Av. de las Ciencias 2000, Parque Industrial Querétaro, Qro.",
        notes: "Parada intermedia en circuito Bajío"
      }
    ]
  },
  {
    id: "DEST-02",
    name: "Ruta Coahuila - Procesos de Maquinado Saltillo",
    clientId: "CLI-PROMAQ",
    clientIds: ["CLI-PROMAQ"],
    clientName: "Procesos de Maquinado S.A. de C.V.",
    originName: "Planta Monterrey - Maquinado",
    originAddress: "Av. Universidad 1200, San Nicolás de los Garza, Nuevo León",
    originLat: 25.7420,
    originLng: -100.3120,
    destinationName: "Procesos de Maquinado Saltillo / Ramos Arizpe",
    address: "Bulevar Isidro López Zertuche 5120, Ramos Arizpe, Coahuila",
    destinationAddress: "Bulevar Isidro López Zertuche 5120, Ramos Arizpe, Coahuila",
    lat: 25.5015,
    lng: -100.8120,
    routeNotes: "Carretera MTY-Saltillo Km 18 - Zona Industrial Ramos Arizpe",
    estimatedKm: 85,
    estimatedMins: 65,
    clientRoutes: [
      {
        clientId: "CLI-PROMAQ",
        clientName: "Procesos de Maquinado S.A. de C.V.",
        originName: "Planta Monterrey - Maquinado",
        originAddress: "Av. Universidad 1200, San Nicolás de los Garza, NL",
        destinationName: "Planta Saltillo - Ramos Arizpe",
        destinationAddress: "Bulevar Isidro López Zertuche 5120, Ramos Arizpe, Coahuila",
        notes: "Rampa 2 de descarga para piezas maquinadas y decapadas"
      }
    ]
  },
  {
    id: "DEST-03",
    name: "Ruta Periférico - Metalsa Apodaca Matriz",
    clientId: "CLI-METALS",
    clientIds: ["CLI-METALS"],
    clientName: "Metalsa S.A. de C.V.",
    originName: "CEDIS Escobedo",
    originAddress: "Carretera a Colombia Km. 5.5, Escobedo, N.L.",
    originLat: 25.7820,
    originLng: -100.4050,
    destinationName: "Metalsa Apodaca Planta Matriz",
    address: "Carretera Miguel Alemán Km. 16.5, Apodaca, Nuevo León",
    destinationAddress: "Carretera Miguel Alemán Km. 16.5, Apodaca, Nuevo León",
    lat: 25.7820,
    lng: -100.1850,
    routeNotes: "Vía Anillo Periférico / Miguel Alemán - Puerta Heavy Duty",
    estimatedKm: 32,
    estimatedMins: 35,
    clientRoutes: [
      {
        clientId: "CLI-METALS",
        clientName: "Metalsa S.A. de C.V.",
        originName: "CEDIS Escobedo",
        originAddress: "Carretera a Colombia Km. 5.5, Escobedo, N.L.",
        destinationName: "Metalsa Apodaca Planta Matriz - Puerta 1",
        destinationAddress: "Carretera Miguel Alemán Km. 16.5, Apodaca, Nuevo León",
        notes: "Acceso con pase de transportista certificado"
      }
    ]
  },
  {
    id: "DEST-04",
    name: "Ruta Metropolitana - Ternium & PROMAQ San Nicolás",
    clientId: "CLI-PROMAQ",
    clientIds: ["CLI-PROMAQ", "CLI-METALS"],
    clientName: "Procesos de Maquinado / Metalsa",
    originName: "Patio de Maniobras Escobedo",
    originAddress: "Libramiento Noreste Km. 21, Escobedo, Nuevo León",
    originLat: 25.7820,
    originLng: -100.4050,
    destinationName: "Ternium San Nicolás - Puerta 4",
    address: "Av. Universidad 1200, San Nicolás de los Garza, Nuevo León",
    destinationAddress: "Av. Universidad 1200, San Nicolás de los Garza, Nuevo León",
    lat: 25.7420,
    lng: -100.3120,
    routeNotes: "Av. Nogalar - Entrada Vehículos Pesados",
    estimatedKm: 18,
    estimatedMins: 25,
    clientRoutes: [
      {
        clientId: "CLI-PROMAQ",
        clientName: "Procesos de Maquinado S.A. de C.V.",
        originName: "Patio de Maniobras Escobedo",
        originAddress: "Libramiento Noreste Km. 21, Escobedo, Nuevo León",
        destinationName: "Ternium San Nicolás - Puerta 4 (Patio Maquinado)",
        destinationAddress: "Av. Universidad 1200, San Nicolás de los Garza, NL",
        notes: "Entrada por bascula 2"
      },
      {
        clientId: "CLI-METALS",
        clientName: "Metalsa S.A. de C.V.",
        originName: "Patio de Maniobras Escobedo",
        originAddress: "Libramiento Noreste Km. 21, Escobedo, Nuevo León",
        destinationName: "Ternium San Nicolás - Puerta 4 (Recepción Estructuras)",
        destinationAddress: "Av. Universidad 1200, San Nicolás de los Garza, NL",
        notes: "Descarga de vigas y perfiles metálicos"
      }
    ]
  }
];

const INITIAL_GOODS: GoodsItem[] = [
  {
    id: "G-MAZDA-01",
    clientId: "CLI-MAZDA",
    code: "MZD-CX5-DOOR",
    description: "Puerta Lateral Trasera Mazda CX-5 (En Blanco)",
    unit: "PZA",
    priceMXN: 8900,
    priceUSD: 520,
    weightKg: 28.5,
    imageUrl: "https://images.unsplash.com/photo-1537462715879-360eeb61a0bc?auto=format&fit=crop&w=300&q=80",
    specifications: "Lámina estampada en frío con recubrimiento electrostático E-Coat."
  },
  {
    id: "G-MAZDA-02",
    clientId: "CLI-MAZDA",
    code: "MZD-M3-HOOD",
    description: "Cofre de Aluminio Mazda 3 Sedan",
    unit: "PZA",
    priceMXN: 11200,
    priceUSD: 655,
    weightKg: 14.2,
    imageUrl: "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&w=300&q=80",
    specifications: "Tratamiento anticorrosivo térmico para granallado e imprimación."
  },
  {
    id: "G-RONAL-01",
    clientId: "CLI-RONAL",
    code: "RON-R18-ALU",
    description: "Rin de Aluminio Fundido 18 pulg Ronal R58",
    unit: "PZA",
    priceMXN: 3400,
    priceUSD: 200,
    weightKg: 11.8,
    imageUrl: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=300&q=80",
    specifications: "Decapado de pintura e inspección por partículas magnéticas."
  },
  {
    id: "G-RONAL-02",
    clientId: "CLI-RONAL",
    code: "RON-R20-FORG",
    description: "Rin Forjado Deportivo 20 pulg Ronal Speed",
    unit: "PZA",
    priceMXN: 5800,
    priceUSD: 340,
    weightKg: 13.5,
    imageUrl: "https://images.unsplash.com/photo-1508962914676-134849a727f0?auto=format&fit=crop&w=300&q=80",
    specifications: "Limpieza por ultrasonido y tratamiento térmico T6."
  },
  {
    id: "G-BMW-01",
    clientId: "CLI-BMW",
    code: "BMW-CR-01",
    description: "Crossbar Rear (Piece), 6x Subframe Assembly",
    unit: "PZA",
    priceMXN: 4500,
    priceUSD: 250,
    weightKg: 0,
    imageUrl: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=200",
    specifications: "Piezas estructurales traseras con acabado anticorrosivo."
  },
  {
    id: "G-BMW-02",
    clientId: "CLI-BMW",
    code: "BMW-FA-02",
    description: "40x Front Axle Carrier, Reinforcement Plate",
    unit: "PZA",
    priceMXN: 6800,
    priceUSD: 380,
    weightKg: 0,
    imageUrl: "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=200",
    specifications: "Placa de refuerzo de eje delantero."
  },
  {
    id: "G-BMW-03",
    clientId: "CLI-BMW",
    code: "BMW-DG-03",
    description: "193x Door Gasket Frame, Side Member Steel",
    unit: "PZA",
    priceMXN: 3200,
    priceUSD: 180,
    weightKg: 0,
    imageUrl: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=200",
    specifications: "Marco de sellado lateral."
  },
  {
    id: "G-BMW-04",
    clientId: "CLI-BMW",
    code: "BMW-HU-04",
    description: "39x Hood upper part (Piece), 12x Pillar Bracket",
    unit: "PZA",
    priceMXN: 5100,
    priceUSD: 285,
    weightKg: 0,
    imageUrl: "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=200",
    specifications: "Soporte de cofre y pilar superior."
  },
  {
    id: "G-BMW-05",
    clientId: "CLI-BMW",
    code: "BMW-ES-05",
    description: "18x Engine Support Beam, Chassis Stiffener",
    unit: "PZA",
    priceMXN: 7400,
    priceUSD: 410,
    weightKg: 0,
    imageUrl: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=200",
    specifications: "Viga soporte de motor y refuerzo estructural."
  },
  {
    id: "G-BMW-06",
    clientId: "CLI-BMW",
    code: "BMW-TL-06",
    description: "25x Trunk Lid Reinforcement & Hinge Carrier",
    unit: "PZA",
    priceMXN: 3900,
    priceUSD: 220,
    weightKg: 0,
    imageUrl: "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=200",
    specifications: "Refuerzo de tapa de cajuela y bisagra."
  },
  {
    id: "G-BMW-07",
    clientId: "CLI-BMW",
    code: "BMW-BT-07",
    description: "15x Battery Floor Tray & EV Module Bracket",
    unit: "PZA",
    priceMXN: 8200,
    priceUSD: 460,
    weightKg: 0,
    imageUrl: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=200",
    specifications: "Bandeja de piso de batería y soporte de módulo EV."
  },
  {
    id: "G-BMW-08",
    clientId: "CLI-BMW",
    code: "BMW-SL-08",
    description: "32x Suspension Link & Wishbone Arm Cast",
    unit: "PZA",
    priceMXN: 5800,
    priceUSD: 320,
    weightKg: 0,
    imageUrl: "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=200",
    specifications: "Brazo de suspensión y horquilla de fundición."
  },
  {
    id: "G-PROMAQ-01",
    clientId: "CLI-PROMAQ",
    code: "EQ-TRM-85",
    description: "Engranes de Transmisión Maquinados (Lote de 50 pzas)",
    unit: "LOTE",
    priceMXN: 12500,
    priceUSD: 735.3,
    weightKg: 250,
    imageUrl: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=200",
    specifications: "Acero templado con recubrimiento anticorrosivo post-decapado."
  },
  {
    id: "G-PROMAQ-02",
    clientId: "CLI-PROMAQ",
    code: "EJ-ACR-40",
    description: "Ejes de Acero Forjado Especiales (Lote de 20 pzas)",
    unit: "LOTE",
    priceMXN: 18900,
    priceUSD: 1111.7,
    weightKg: 400,
    imageUrl: "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=200",
    specifications: "Maquinado de alta precisión para chasis automotriz."
  }
];

// Generator for all 49 BMW Folios requested with complete traceability and accurate data
function generateBMW49Folios(): Folio[] {
  const bmwPartsCatalog = [
    { code: "G-BMW-01", desc: "24x Crossbar Rear (Piece), 6x Subframe Assembly", priceMXN: 4500, priceUSD: 250, defaultQty: 30 },
    { code: "G-BMW-02", desc: "40x Front Axle Carrier, Reinforcement Plate", priceMXN: 6800, priceUSD: 380, defaultQty: 40 },
    { code: "G-BMW-03", desc: "193x Door Gasket Frame, Side Member Steel", priceMXN: 3200, priceUSD: 180, defaultQty: 193 },
    { code: "G-BMW-04", desc: "39x Hood upper part (Piece), 12x Pillar Bracket", priceMXN: 5100, priceUSD: 285, defaultQty: 51 },
    { code: "G-BMW-05", desc: "18x Engine Support Beam, Chassis Stiffener", priceMXN: 7400, priceUSD: 410, defaultQty: 18 },
    { code: "G-BMW-06", desc: "25x Trunk Lid Reinforcement & Hinge Carrier", priceMXN: 3900, priceUSD: 220, defaultQty: 25 },
    { code: "G-BMW-07", desc: "15x Battery Floor Tray & EV Module Bracket", priceMXN: 8200, priceUSD: 460, defaultQty: 15 },
    { code: "G-BMW-08", desc: "32x Suspension Link & Wishbone Arm Cast", priceMXN: 5800, priceUSD: 320, defaultQty: 32 }
  ];

  // Specific 49 IDs mapped
  const folioIds: string[] = [];
  for (let i = 1; i <= 45; i++) {
    const numStr = (19265300 + i).toString();
    folioIds.push(numStr);
  }
  folioIds.push("19284957");
  folioIds.push("19284958");
  folioIds.push("19284969 - D1");
  folioIds.push("19284969 - D2");

  const statuses: Folio['status'][] = [
    'ENTREGA_PLANTA', 'ENTREGA_PLANTA', 'ENTREGA_PLANTA', 'SALIDA_CEDIS',
    'INSPECCION', 'DECAPADO_LAVADO', 'DECAPADO_TERMICO_QUIMICO', 'ENTRADA_CEDIS', 'RECOLECCION'
  ];

  return folioIds.map((fId, idx) => {
    const part = bmwPartsCatalog[idx % bmwPartsCatalog.length];
    const isSLP = idx % 2 === 0;
    const isPuebla = !isSLP;
    const costCenterId = isSLP ? "CC-BMW-SLP" : "CC-BMW-PUE";
    const costCenterName = isSLP ? "Sede San Luis Potosí - Planta BMW" : "Sede Puebla - Centro de Distribución BMW";
    const destination = isSLP ? "SLP" : "PUEBLA";
    const status = idx < 38 ? 'ENTREGA_PLANTA' : statuses[idx % statuses.length];
    
    // Distribute all 49 BMW folios across Mayo, Junio, Julio, Agosto, and Septiembre 2026
    let month = 4; // Mayo (0-indexed)
    let day = 4;
    let hasDevolucion = false;
    let devolucionQty = 0;
    let devolucionDetail = '';

    if (idx < 10) {
      // MAYO 2026 (10 folios: idx 0..9)
      month = 4;
      day = 4 + (idx * 2);
    } else if (idx < 20) {
      // JUNIO 2026 (10 folios: idx 10..19)
      month = 5;
      day = 2 + ((idx - 10) * 2);
    } else if (idx < 30) {
      // JULIO 2026 (10 folios: idx 20..29)
      month = 6;
      day = 3 + ((idx - 20) * 2);
    } else if (idx < 40) {
      // AGOSTO 2026 (10 folios: idx 30..39)
      month = 7;
      day = 3 + ((idx - 30) * 2);
    } else {
      // SEPTIEMBRE 2026 (9 folios: idx 40..48)
      month = 8;
      day = 2 + Math.floor((idx - 40) * 1.6);
    }

    const folioDateObj = new Date(2026, month, Math.min(28, Math.max(1, Math.floor(day))), 10, 30, 0);
    const dateIso = folioDateObj.toISOString();
    const dateStr = dateIso.slice(0, 10);

    const qty = part.defaultQty;
    const totalMXN = qty * part.priceMXN;
    const totalUSD = qty * part.priceUSD;
    const hasGranallado = idx % 3 === 0;

    // Realistic operational turnaround & transit hours from RECOLECCION to ENTREGA_PLANTA
    let elapsedHours = 14 + Number(((idx * 2.3) % 7.5).toFixed(1)); // Typically 14.0 to 21.5 hours
    if (idx === 8) {
      elapsedHours = 27.5; // Folio 19265309 (Mayo): Demora por maniobra de estiba y tráfico pesado en Carretera 57 (> 24h)
    } else if (idx === 25) {
      elapsedHours = 28.0; // Folio 19265326 (Julio): Demora logística en andén de recepción en planta (> 24h)
    }
    const recoleccionTime = new Date(folioDateObj.getTime() - elapsedHours * 3600000);
    const entradaCedisTime = new Date(recoleccionTime.getTime() + 2.5 * 3600000);
    const decapadoTime = new Date(folioDateObj.getTime() - 4.5 * 3600000);

    return {
      id: fId,
      date: dateStr,
      qrCodeUrl: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(fId)}`,
      clientId: "CLI-BMW",
      clientName: "BMW GROUP",
      costCenterId,
      costCenterName,
      createdDate: dateIso,
      shipmentDate: dateStr,
      createdBy: "admin@snfsic.com",
      status,
      hasGranallado,
      items: [
        {
          goodsItemId: part.code,
          description: fId.includes('D1') ? `Destino 1 [PUEBLA]: ${part.desc}` : fId.includes('D2') ? `Destino 2 [SLP]: ${part.desc}` : part.desc,
          quantity: qty,
          unitPriceMXN: part.priceMXN,
          unitPriceUSD: part.priceUSD,
          totalMXN,
          totalUSD
        }
      ],
      destination,
      origin: isSLP ? "San Luis Potosí" : "CEDIS Puebla",
      driverName: isSLP ? "Chofer SLP - Unidad 04" : "Chofer Puebla - Unidad 09",
      economicNumber: isSLP ? "TRK-SLP-401" : "TRK-PUE-109",
      history: [
        {
          stage: "RECOLECCION" as ProcessStage,
          timestamp: recoleccionTime.toISOString(),
          operatorName: isSLP ? "Chofer BMW SLP" : "Chofer BMW Puebla",
          notes: `Lote recibido en planta para proceso de decapado industrial (${elapsedHours <= 24 ? 'En tiempo estándar' : 'Demora logística registrada'})`
        },
        {
          stage: "ENTRADA_CEDIS" as ProcessStage,
          timestamp: entradaCedisTime.toISOString(),
          operatorName: "Supervisor CEDIS",
          notes: "Inspección de entrada completada y registrada"
        },
        {
          stage: "DECAPADO_TERMICO_QUIMICO" as ProcessStage,
          timestamp: decapadoTime.toISOString(),
          operatorName: "Operador Horno Termoquímico",
          notes: "Proceso termoquímico e inhibición aplicados correctamente"
        },
        ...(status === 'ENTREGA_PLANTA' ? [{
          stage: "ENTREGA_PLANTA" as ProcessStage,
          timestamp: dateIso,
          operatorName: isSLP ? "Chofer SLP" : "Chofer Puebla",
          notes: `Entregado satisfactoriamente en ${destination} con acuse de recibo. Tiempo de ciclo: ${elapsedHours.toFixed(1)} hrs (${elapsedHours <= 24 ? 'Cumple SLA <=24h' : 'Excede SLA 24h'})`
        }] : []),
        ...(hasDevolucion ? [{
          stage: "DEVOLUCION" as ProcessStage,
          timestamp: new Date(folioDateObj.getTime() + 4 * 3600000).toISOString(),
          operatorName: isSLP ? "Inspector Calidad SLP" : "Inspector Calidad Puebla",
          returnDetails: devolucionDetail,
          returnType: 'PARCIAL' as const,
          returnedItems: [
            {
              goodsItemId: part.code,
              description: part.desc,
              quantityReturned: devolucionQty,
              originalQuantity: qty
            }
          ]
        }] : [])
      ]
    };
  });
}

export const SPECIAL_BMW_FOLIOS: Folio[] = [
  {
    id: "19769636",
    date: "2026-09-18",
    qrCodeUrl: "https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=19769636",
    clientId: "CLI-BMW",
    clientName: "BMW GROUP",
    costCenterId: "CC-BMW-SLP",
    costCenterName: "Sede San Luis Potosí - Planta BMW",
    createdDate: new Date(Date.now() - 18 * 3600000).toISOString(),
    shipmentDate: "2026-09-18",
    createdBy: "admin@snfsic.com",
    status: "INSPECCION",
    hasGranallado: true,
    items: [
      {
        goodsItemId: "G-BMW-01",
        description: "24x Crossbar Rear (Piece), 6x Subframe Assembly",
        quantity: 30,
        unitPriceMXN: 4500,
        unitPriceUSD: 250,
        totalMXN: 135000,
        totalUSD: 7500
      }
    ],
    destination: "Planta SLP - Andén de Recepción",
    origin: "San Luis Potosí",
    driverName: "Chofer SLP - Unidad 04",
    economicNumber: "TRK-SLP-401",
    history: [
      {
        stage: "RECOLECCION",
        timestamp: new Date(Date.now() - 18 * 3600000).toISOString(),
        operatorName: "Chofer BMW SLP",
        notes: "Lote recibido en planta para proceso de decapado industrial en tiempo estándar"
      },
      {
        stage: "ENTRADA_CEDIS",
        timestamp: new Date(Date.now() - 14 * 3600000).toISOString(),
        operatorName: "Supervisor CEDIS",
        notes: "Recepción en patio y verificación de peso y conteo de piezas completada"
      },
      {
        stage: "DECAPADO_TERMICO_QUIMICO",
        timestamp: new Date(Date.now() - 8 * 3600000).toISOString(),
        operatorName: "Operador Horno Termoquímico",
        notes: "Proceso termoquímico e inhibición aplicados correctamente"
      },
      {
        stage: "INSPECCION",
        timestamp: new Date(Date.now() - 2 * 3600000).toISOString(),
        operatorName: "Inspector Calidad SLP",
        notes: "Inspección de acabado superficial y certificación dimensional en proceso"
      }
    ]
  },
  {
    id: "19770162",
    date: "2026-09-22",
    qrCodeUrl: "https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=19770162",
    clientId: "CLI-BMW",
    clientName: "BMW GROUP",
    costCenterId: "CC-BMW-SLP",
    costCenterName: "Sede San Luis Potosí - Planta BMW",
    createdDate: new Date(Date.now() - 22 * 3600000).toISOString(),
    shipmentDate: "2026-09-22",
    createdBy: "admin@snfsic.com",
    status: "SALIDA_CEDIS",
    hasGranallado: true,
    items: [
      {
        goodsItemId: "G-BMW-02",
        description: "40x Front Axle Carrier, Reinforcement Plate",
        quantity: 40,
        unitPriceMXN: 6800,
        unitPriceUSD: 380,
        totalMXN: 272000,
        totalUSD: 15200
      }
    ],
    destination: "Planta SLP - Ensamble",
    origin: "San Luis Potosí",
    driverName: "Chofer SLP - Unidad 02",
    economicNumber: "TRK-SLP-402",
    history: [
      {
        stage: "RECOLECCION",
        timestamp: new Date(Date.now() - 22 * 3600000).toISOString(),
        operatorName: "Chofer BMW SLP",
        notes: "Lote recibido en planta para proceso de decapado industrial"
      },
      {
        stage: "ENTRADA_CEDIS",
        timestamp: new Date(Date.now() - 17 * 3600000).toISOString(),
        operatorName: "Supervisor CEDIS",
        notes: "Entrada a CEDIS registrada y pesaje verificado"
      },
      {
        stage: "DECAPADO_TERMICO_QUIMICO",
        timestamp: new Date(Date.now() - 11 * 3600000).toISOString(),
        operatorName: "Operador Horno Termoquímico",
        notes: "Tratamiento térmico concluido satisfactoriamente"
      },
      {
        stage: "INSPECCION",
        timestamp: new Date(Date.now() - 5 * 3600000).toISOString(),
        operatorName: "Inspector Calidad SLP",
        notes: "Calidad superficial conforme al 100%. Liberado para embarque."
      },
      {
        stage: "SALIDA_CEDIS",
        timestamp: new Date(Date.now() - 1 * 3600000).toISOString(),
        operatorName: "Operador Logístico CEDIS",
        notes: "Salida de CEDIS autorizada. Unidad en ruta de entrega a planta."
      }
    ]
  }
];

const BMW_49_FOLIOS: Folio[] = [...generateBMW49Folios(), ...SPECIAL_BMW_FOLIOS];

const INITIAL_FOLIOS: Folio[] = [
  {
    id: "MZD-2026-001",
    qrCodeUrl: "https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=MZD-2026-001",
    clientId: "CLI-MAZDA",
    clientName: "Mazda Motor de México",
    costCenterId: "CC-MAZDA-BAJIO",
    costCenterName: "Sede Bajío - Mazda Planta Salamanca",
    createdDate: new Date(Date.now() - 1 * 3600000).toISOString(),
    createdBy: "logistica@mazda.com.mx",
    status: "ENTREGA_PLANTA",
    hasGranallado: true,
    items: [
      {
        goodsItemId: "G-MAZDA-01",
        description: "Puerta Lateral Trasera Mazda CX-5 (En Blanco)",
        quantity: 20,
        unitPriceMXN: 8900,
        unitPriceUSD: 520,
        totalMXN: 178000,
        totalUSD: 10400
      }
    ],
    destination: "Planta Salamanca - Ensamble",
    origin: "Sede Bajío",
    history: [
      {
        stage: "RECOLECCION",
        timestamp: new Date(Date.now() - 4 * 3600000).toISOString(),
        operatorName: "Chofer Bajío",
        notes: "Lote recibido para proceso E-Coat"
      },
      {
        stage: "ENTREGA_PLANTA",
        timestamp: new Date(Date.now() - 1 * 3600000).toISOString(),
        operatorName: "Ing. Kenji Takahashi",
        notes: "Entregado a línea de ensamble Salamanca"
      }
    ]
  },
  {
    id: "RON-2026-088",
    qrCodeUrl: "https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=RON-2026-088",
    clientId: "CLI-RONAL",
    clientName: "Ronal Group México",
    costCenterId: "CC-RONAL-QRO",
    costCenterName: "Sede Querétaro - Planta Ronal Group",
    createdDate: new Date(Date.now() - 3 * 3600000).toISOString(),
    createdBy: "operaciones@ronalgroup.com",
    status: "DECAPADO_LAVADO",
    hasGranallado: true,
    items: [
      {
        goodsItemId: "G-RONAL-01",
        description: "Rin de Aluminio Fundido 18 pulg Ronal R58",
        quantity: 50,
        unitPriceMXN: 3400,
        unitPriceUSD: 200,
        totalMXN: 170000,
        totalUSD: 10000
      }
    ],
    destination: "Planta Querétaro - Autoclave",
    origin: "Sede Querétaro",
    history: [
      {
        stage: "RECOLECCION",
        timestamp: new Date(Date.now() - 5 * 3600000).toISOString(),
        operatorName: "Chofer Querétaro",
        notes: "Recibido lote R58"
      },
      {
        stage: "DECAPADO_LAVADO",
        timestamp: new Date(Date.now() - 3 * 3600000).toISOString(),
        operatorName: "Operador Lavado",
        notes: "En lavadora industrial ultrasónica"
      }
    ]
  },
  ...BMW_49_FOLIOS,
  {
    id: "F-102501",
    qrCodeUrl: "https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=F-102501",
    clientId: "CLI-PROMAQ",
    clientName: "Procesos de Maquinado S.A. de C.V.",
    costCenterId: "CC-PROMAQ-SLT",
    costCenterName: "Planta Saltillo - Ensamble",
    createdDate: new Date(Date.now() - 4 * 3600000).toISOString(),
    createdBy: "admin@snfsic.com",
    status: "SALIDA_CEDIS",
    hasGranallado: false,
    items: [
      {
        goodsItemId: "G-PROMAQ-01",
        description: "Engranes de Transmisión Maquinados (Lote de 50 pzas)",
        quantity: 2,
        unitPriceMXN: 12500,
        unitPriceUSD: 735.3,
        totalMXN: 25000,
        totalUSD: 1470.6
      }
    ],
    notes: "Entrega urgente para línea de ensamble Coahuila.",
    history: [
      {
        stage: "RECOLECCION",
        timestamp: new Date(Date.now() - 4 * 3600000).toISOString(),
        operatorName: "Ing. Pedro Borja",
        notes: "Lote recolectado en planta del cliente."
      },
      {
        stage: "ENTRADA_CEDIS",
        timestamp: new Date(Date.now() - 3.5 * 3600000).toISOString(),
        operatorName: "Supervisor CEDIS",
        notes: "Ingreso e inspección visual aprobada en CEDIS."
      },
      {
        stage: "DECAPADO_TERMICO_QUIMICO",
        timestamp: new Date(Date.now() - 2.5 * 3600000).toISOString(),
        operatorName: "Operador Horno 2",
        notes: "Decapado térmico completado sin novedades."
      },
      {
        stage: "DECAPADO_LAVADO",
        timestamp: new Date(Date.now() - 2 * 3600000).toISOString(),
        operatorName: "Operador Lavado",
        notes: "Lavado a presión e inhibidor aplicados."
      },
      {
        stage: "INSPECCION",
        timestamp: new Date(Date.now() - 1.5 * 3600000).toISOString(),
        operatorName: "Inspec. Calidad",
        notes: "Inspección micrométrica aprobada 100%."
      },
      {
        stage: "SALIDA_CEDIS",
        timestamp: new Date(Date.now() - 0.5 * 3600000).toISOString(),
        operatorName: "Despachador CEDIS",
        notes: "Cargado en camión económico N° 105. Chofer asignado."
      }
    ],
    etaMinutes: 25,
    currentLat: 25.5640,
    currentLng: -100.6750,
    shipmentDate: new Date().toISOString().split('T')[0],
    destination: "Planta Saltillo - Ensamble",
    origin: "CEDIS Monterrey",
    driverName: "Simeón Juárez",
    economicNumber: "TRK-105-COA"
  },
  {
    id: "F-102502",
    qrCodeUrl: "https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=F-102502",
    clientId: "CLI-PROMAQ",
    clientName: "Procesos de Maquinado S.A. de C.V.",
    costCenterId: "CC-PROMAQ-MTY",
    costCenterName: "Planta Monterrey - Maquinado",
    createdDate: new Date(Date.now() - 6 * 3600000).toISOString(),
    createdBy: "admin@snfsic.com",
    status: "INSPECCION",
    hasGranallado: true,
    items: [
      {
        goodsItemId: "G-PROMAQ-02",
        description: "Ejes de Acero Forjado Especiales (Lote de 20 pzas)",
        quantity: 1,
        unitPriceMXN: 18900,
        unitPriceUSD: 1111.7,
        totalMXN: 18900,
        totalUSD: 1111.7
      }
    ],
    notes: "Requiere granallado post-decapado térmico.",
    history: [
      {
        stage: "RECOLECCION",
        timestamp: new Date(Date.now() - 6 * 3600000).toISOString(),
        operatorName: "Ing. Pedro Borja",
        notes: "Recolectado en Planta Monterrey."
      },
      {
        stage: "ENTRADA_CEDIS",
        timestamp: new Date(Date.now() - 5 * 3600000).toISOString(),
        operatorName: "Supervisor CEDIS",
        notes: "Ingreso registrado. Granallado programado."
      },
      {
        stage: "DECAPADO_TERMICO_QUIMICO",
        timestamp: new Date(Date.now() - 3 * 3600000).toISOString(),
        operatorName: "Operador Horno 1",
        notes: "Decapado e inhibidor aplicados."
      },
      {
        stage: "INSPECCION",
        timestamp: new Date(Date.now() - 1 * 3600000).toISOString(),
        operatorName: "Inspec. Calidad",
        notes: "Pruebas de dureza mecánica en proceso."
      }
    ],
    origin: "CEDIS Monterrey",
    destination: "Planta Monterrey - Maquinado"
  },
  {
    id: "F-102503",
    qrCodeUrl: "https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=F-102503",
    clientId: "CLI-PROMAQ",
    clientName: "Procesos de Maquinado S.A. de C.V.",
    costCenterId: "CC-PROMAQ-MTY",
    costCenterName: "Planta Monterrey - Maquinado",
    createdDate: new Date(Date.now() - 24 * 3600000).toISOString(),
    createdBy: "admin@snfsic.com",
    status: "ENTREGA_PLANTA",
    hasGranallado: false,
    items: [
      {
        goodsItemId: "G-PROMAQ-01",
        description: "Engranes de Transmisión Maquinados (Lote de 50 pzas)",
        quantity: 1,
        unitPriceMXN: 12500,
        unitPriceUSD: 735.3,
        totalMXN: 12500,
        totalUSD: 735.3
      }
    ],
    notes: "Procesado estandar exitoso.",
    history: [
      {
        stage: "RECOLECCION",
        timestamp: new Date(Date.now() - 24 * 3600000).toISOString(),
        operatorName: "Ing. Pedro Borja",
        notes: "Recolectado en Planta Monterrey."
      },
      {
        stage: "ENTREGA_PLANTA",
        timestamp: new Date(Date.now() - 18 * 3600000).toISOString(),
        operatorName: "Chofer Felipe",
        notes: "Piezas entregadas y firmadas por almacén."
      }
    ],
    origin: "CEDIS Monterrey",
    destination: "Planta Monterrey - Maquinado"
  }
];

const INITIAL_PRE_INVOICES: PreInvoice[] = [
  {
    id: "PRE-102503",
    folioId: "F-102503",
    clientId: "CLI-PROMAQ",
    clientName: "Procesos de Maquinado S.A. de C.V.",
    issueDate: new Date().toISOString().split('T')[0],
    subtotalMXN: 12500,
    subtotalUSD: 735.3,
    taxMXN: 2000,
    taxUSD: 117.65,
    totalMXN: 14500,
    totalUSD: 852.95,
    status: 'Draft',
    items: [
      {
        description: "Engranes de Transmisión Maquinados (Lote de 50 pzas) - Decapado e inhibidor térmico",
        quantity: 1,
        priceMXN: 12500,
        priceUSD: 735.3,
        totalMXN: 12500,
        totalUSD: 735.3
      }
    ]
  }
];

const INITIAL_MEETINGS: MeetMeeting[] = [
  {
    id: 'm-1001',
    title: 'Revisión Técnica Control de Calidad Lote SLP-01',
    clientId: 'cli-bmw-slp',
    clientName: 'BMW Group Planta San Luis Potosí',
    date: new Date().toISOString().split('T')[0],
    time: '12:00',
    meetLink: 'https://meet.google.com/snf-traz-slp',
    meetingType: 'Control de Calidad',
    notes: 'Aclaración de tiempos de decapado térmico y entrega de lote de prueba.',
    requestedByClient: false,
    status: 'Scheduled',
    createdAt: new Date().toISOString()
  },
  {
    id: 'm-1002',
    title: 'Solicitud de Ajuste de Horario de Recolección',
    clientId: 'cli-promaq',
    clientName: 'Procesos de Maquinado S.A. de C.V.',
    date: new Date().toISOString().split('T')[0],
    time: '16:30',
    meetLink: 'https://meet.google.com/snf-prom-req',
    meetingType: 'Urgencia Logística',
    notes: 'El cliente requiere validar la liberación del folio F-102501 y coordinar transporte.',
    requestedByClient: true,
    status: 'Requested',
    createdAt: new Date().toISOString()
  }
];

const INITIAL_CALENDAR_EVENTS: CalendarEvent[] = [];

const INITIAL_PO_RECORDS: PoRecord[] = [
  {
    id: "PO-2025-BMW-Q4",
    poNumber: "PO-2025-BMW-Q4",
    clientId: "CLI-BMW",
    clientName: "BMW GROUP",
    annualBudgetMXN: 1200000.00,
    previousBalanceMXN: 54917.88,
    manualAdjustmentsMXN: 0,
    totalNetBudgetMXN: 1254917.88,
    totalDeliveredMXN: 1024600.00,
    balanceMXN: 230317.88,
    balanceType: 'SALDO_A_FAVOR',
    status: 'CERRADA',
    linkedFolioIds: ["19265345", "19284969 - D1", "19284969 - D2"],
    totalEncapsulatedPieces: 263,
    encapsulatedFolios: [
      {
        folioId: "19265345",
        createdDate: "2025-12-10T14:30:00.000Z",
        shipmentDate: "2025-12-10",
        costCenterId: "CC-BMW-PUE",
        costCenterName: "Puebla - Centro de Distribución",
        destination: "PUEBLA",
        driverName: "Chofer Puebla",
        grStatus: "CONFIRMADO",
        status: "ENTREGA_PLANTA",
        totalPieces: 30,
        totalMXN: 135000,
        items: [
          {
            description: "24x Crossbar Rear (Piece), 6x Subframe Assembly",
            quantity: 30,
            unitPriceMXN: 4500,
            totalMXN: 135000
          }
        ]
      },
      {
        folioId: "19284969 - D1",
        createdDate: "2025-12-18T10:15:00.000Z",
        shipmentDate: "2025-12-18",
        costCenterId: "CC-BMW-PUE",
        costCenterName: "Puebla - Centro de Distribución",
        destination: "PUEBLA",
        driverName: "Chofer Puebla",
        grStatus: "CONFIRMADO",
        status: "ENTREGA_PLANTA",
        totalPieces: 40,
        totalMXN: 272000,
        items: [
          {
            description: "Destino 1 [PUEBLA]: 40x Front Axle Carrier, Reinforcement Plate",
            quantity: 40,
            unitPriceMXN: 6800,
            totalMXN: 272000
          }
        ]
      },
      {
        folioId: "19284969 - D2",
        createdDate: "2025-12-22T09:00:00.000Z",
        shipmentDate: "2025-12-22",
        costCenterId: "CC-BMW-SLP",
        costCenterName: "Sede San Luis Potosí - Planta BMW",
        destination: "SLP",
        driverName: "Chofer SLP",
        grStatus: "CONFIRMADO",
        status: "ENTREGA_PLANTA",
        totalPieces: 193,
        totalMXN: 617600,
        items: [
          {
            description: "Destino 2 [SLP]: 193x Door Gasket Frame, Side Member Steel",
            quantity: 193,
            unitPriceMXN: 3200,
            totalMXN: 617600
          }
        ]
      }
    ],
    notes: "Cierre de ejercicio Q4 2025. Conciliación con auditoría de recepción en plantas SLP y Puebla.",
    createdAt: "2025-10-01T08:00:00.000Z",
    lastUpdated: "2025-12-31T18:00:00.000Z",
    closedAt: "2025-12-31T18:00:00.000Z",
    closedBy: "admin@snfsic.com",
    closureNotes: "Orden de compra cerrada con remanente a favor. Saldo transferido al ejercicio 2026.",
    carriedToNextPoNumber: "PO-2026-ANUAL-BMW",
    surplusCarriedMXN: 230317.88
  },
  {
    id: "PO-2026-ANUAL-BMW",
    poNumber: "PO-2026-ANUAL-BMW",
    clientId: "CLI-BMW",
    clientName: "BMW GROUP",
    annualBudgetMXN: 1254917.08,
    previousBalanceMXN: 230317.88,
    manualAdjustmentsMXN: 0,
    totalNetBudgetMXN: 1485234.96,
    totalDeliveredMXN: 1024600.00,
    balanceMXN: 460634.96,
    balanceType: 'SALDO_A_FAVOR',
    status: 'ABIERTA',
    linkedFolioIds: ["19265345", "19284969 - D1", "19284969 - D2"],
    totalEncapsulatedPieces: 263,
    notes: "Presupuesto Asignado Control Financiero Anual 2026",
    createdAt: "2026-01-02T08:00:00.000Z",
    lastUpdated: new Date().toISOString()
  },
  {
    id: "PO-2026-MAZDA-01",
    poNumber: "PO-2026-MAZDA-01",
    clientId: "CLI-MAZDA",
    clientName: "Mazda Motor de México",
    annualBudgetMXN: 850000.00,
    previousBalanceMXN: 42300.00,
    manualAdjustmentsMXN: 0,
    totalNetBudgetMXN: 892300.00,
    totalDeliveredMXN: 540000.00,
    balanceMXN: 352300.00,
    balanceType: 'SALDO_A_FAVOR',
    status: 'ABIERTA',
    linkedFolioIds: ["MZD-2026-001", "MZD-2026-002"],
    totalEncapsulatedPieces: 150,
    notes: "Orden de Compra Planta Salamanca 2026",
    createdAt: "2026-01-05T08:00:00.000Z",
    lastUpdated: new Date().toISOString()
  },
  {
    id: "PO-2026-RONAL-01",
    poNumber: "PO-2026-RONAL-01",
    clientId: "CLI-RONAL",
    clientName: "Ronal Group México",
    annualBudgetMXN: 600000.00,
    previousBalanceMXN: 0,
    manualAdjustmentsMXN: 0,
    totalNetBudgetMXN: 600000.00,
    totalDeliveredMXN: 170000.00,
    balanceMXN: 430000.00,
    balanceType: 'SALDO_A_FAVOR',
    status: 'ABIERTA',
    linkedFolioIds: ["RON-2026-088"],
    totalEncapsulatedPieces: 50,
    notes: "PO Operaciones Querétaro 2026",
    createdAt: "2026-01-10T08:00:00.000Z",
    lastUpdated: new Date().toISOString()
  }
];

// Clean object properties to prevent Firestore errors with undefined values
export function cleanForFirestore(obj: any): any {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object') return obj;
  if (obj instanceof Date) return obj.toISOString();
  if (Array.isArray(obj)) {
    return obj.map(item => cleanForFirestore(item)).filter(item => item !== null && item !== undefined);
  }
  
  const cleaned: any = {};
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    if (val !== undefined) {
      cleaned[key] = typeof val === 'object' && val !== null ? cleanForFirestore(val) : val;
    }
  }
  return cleaned;
}

// Helper to handle client-side/firestore fallback
async function getCollectionData<T>(collectionName: string, initialData: T[]): Promise<T[]> {
  try {
    const colRef = collection(db, collectionName);
    const snapshot = await getDocs(colRef);
    
    if (snapshot.empty) {
      // Check if collection was explicitly cleared
      const wasCleared = localStorage.getItem(`snfsic_cleared_${collectionName}`) === 'true';
      if (wasCleared) {
        return [];
      }

      // Seed Firestore dynamically on initial setup
      const local = localStorage.getItem(`snfsic_${collectionName}`) || localStorage.getItem(`trazalog_${collectionName}`);
      let seedData = initialData;
      if (local) {
        try {
          const parsed = JSON.parse(local);
          if (Array.isArray(parsed) && parsed.length > 0) {
            seedData = parsed;
          }
        } catch (e) {
          console.error("Failed parsing local storage", e);
        }
      }
      for (const item of seedData) {
        const itemId = (item as any).id || (item as any).uid;
        const cleaned = cleanForFirestore(item);
        if (itemId) {
          await setDoc(doc(db, collectionName, itemId), cleaned);
        } else {
          await addDoc(colRef, cleaned);
        }
      }
      localStorage.setItem(`snfsic_${collectionName}`, JSON.stringify(seedData));
      return seedData;
    }

    const items: T[] = [];
    snapshot.forEach((docSnap) => {
      items.push({ id: docSnap.id, ...docSnap.data() } as any);
    });

    // Only sync items that were explicitly queued as pending offline saves
    const queue = dataService.getPendingActions();
    const pendingSaves = queue.filter(a => a.action === 'save' && a.collectionName === collectionName && a.data);

    if (pendingSaves.length > 0) {
      for (const action of pendingSaves) {
        const targetId = action.targetId;
        const exists = items.some((i: any) => i.id === targetId || i.uid === targetId);
        if (!exists && action.data) {
          try {
            await setDoc(doc(db, collectionName, targetId), cleanForFirestore(action.data));
            items.push({ id: targetId, ...action.data });
            dataService.removePendingAction(action.id);
          } catch (e) {
            console.warn(`Failed syncing pending save for ${collectionName}/${targetId}:`, e);
          }
        }
      }
    }

    // Always keep localStorage updated to mirror Firestore (removing deleted items)
    localStorage.setItem(`snfsic_${collectionName}`, JSON.stringify(items));

    return items;
  } catch (error) {
    console.warn(`Firestore collection [${collectionName}] failed, using local fallback.`, error);
    // Use localStorage as dual protection fallback so the user experience is incredibly reliable
    const local = localStorage.getItem(`snfsic_${collectionName}`) || localStorage.getItem(`trazalog_${collectionName}`);
    if (local) {
      return JSON.parse(local);
    }
    localStorage.setItem(`snfsic_${collectionName}`, JSON.stringify(initialData));
    return initialData;
  }
}

// Pending Action Queue Management
let pendingActionListeners: ((queue: PendingAction[]) => void)[] = [];
let isSyncingPendingQueue = false;

function notifyPendingActionsListeners() {
  const queue = dataService.getPendingActions();
  pendingActionListeners.forEach(listener => listener(queue));
}

async function saveItemData<T>(collectionName: string, id: string, data: T): Promise<void> {
  let success = false;
  const cleanedData = cleanForFirestore(data);
  
  const isOnline = typeof navigator !== 'undefined' && navigator.onLine;
  if (isOnline) {
    try {
      await setDoc(doc(db, collectionName, id), cleanedData);
      success = true;
      // If it succeeded, remove any matching save action from queue
      const actionId = `save_${collectionName}_${id}`;
      dataService.removePendingAction(actionId);
    } catch (error) {
      console.warn(`Firestore save to [${collectionName}] failed, fallback to queueing.`, error);
    }
  }

  if (!success) {
    // Add to local offline pending action queue
    dataService.addPendingAction('save', collectionName, id, cleanedData);
  }

  // Always synchronize to localStorage as robust dual persistence
  const local = localStorage.getItem(`snfsic_${collectionName}`) || localStorage.getItem(`trazalog_${collectionName}`);
  const items = local ? JSON.parse(local) : [];
  const idx = items.findIndex((i: any) => i.id === id || i.uid === id);
  if (idx >= 0) {
    items[idx] = { id, ...cleanedData };
  } else {
    items.push({ id, ...cleanedData });
  }
  localStorage.setItem(`snfsic_${collectionName}`, JSON.stringify(items));
}

async function deleteItemData(collectionName: string, id: string): Promise<void> {
  let success = false;

  const isOnline = typeof navigator !== 'undefined' && navigator.onLine;
  if (isOnline) {
    try {
      await deleteDoc(doc(db, collectionName, id));
      success = true;
      // If it succeeded, remove any matching delete action from queue
      const actionId = `delete_${collectionName}_${id}`;
      dataService.removePendingAction(actionId);
    } catch (error) {
      console.warn(`Firestore delete from [${collectionName}] failed, fallback to queueing.`, error);
    }
  }

  if (!success) {
    // Add to local offline pending action queue
    dataService.addPendingAction('delete', collectionName, id, null);
  }

  // Always synchronize to localStorage as robust dual persistence
  const local = localStorage.getItem(`snfsic_${collectionName}`) || localStorage.getItem(`trazalog_${collectionName}`);
  if (local) {
    const items = JSON.parse(local);
    // filter handles either item.id or item.uid
    const filtered = items.filter((i: any) => i.id !== id && i.uid !== id);
    localStorage.setItem(`snfsic_${collectionName}`, JSON.stringify(filtered));
  }
}

// Data Service Exports
export const dataService = {
  // Pending queue operations
  getPendingActions(): PendingAction[] {
    const local = localStorage.getItem('snfsic_pending_actions');
    return local ? JSON.parse(local) : [];
  },

  subscribeToPendingActions(listener: (queue: PendingAction[]) => void): () => void {
    pendingActionListeners.push(listener);
    // Initial emit
    listener(this.getPendingActions());
    return () => {
      pendingActionListeners = pendingActionListeners.filter(l => l !== listener);
    };
  },

  addPendingAction(action: 'save' | 'delete', collectionName: string, targetId: string, data?: any, description?: string) {
    const queue = this.getPendingActions();
    
    let finalDesc = description || '';
    if (!finalDesc) {
      const actionVerb = action === 'save' ? 'Guardar' : 'Eliminar';
      let objName = collectionName;
      if (collectionName === 'clients') objName = 'Cliente';
      else if (collectionName === 'costCenters') objName = 'Centro de Costos';
      else if (collectionName === 'goods') objName = 'Catálogo de Bienes';
      else if (collectionName === 'folios') objName = 'Folio Logístico';
      else if (collectionName === 'preInvoices') objName = 'Prefactura';
      else if (collectionName === 'meetings') objName = 'Reunión';
      else if (collectionName === 'calendarEvents') objName = 'Evento de Calendario';
      else if (collectionName === 'activityLogs') objName = 'Bitácora de Actividad';
      else if (collectionName === 'users') objName = 'Perfil de Usuario';
      
      const itemTitle = data?.name || data?.title || data?.id || targetId;
      finalDesc = `${actionVerb} ${objName}: ${itemTitle}`;
    }

    const actionId = `${action}_${collectionName}_${targetId}`;
    const filteredQueue = queue.filter(item => item.id !== actionId);
    
    const cleanedData = data ? cleanForFirestore(data) : data;
    const newAction: PendingAction = {
      id: actionId,
      action,
      collectionName,
      targetId,
      data: cleanedData,
      timestamp: new Date().toISOString(),
      description: finalDesc
    };

    filteredQueue.push(newAction);
    localStorage.setItem('snfsic_pending_actions', JSON.stringify(filteredQueue));
    notifyPendingActionsListeners();

    // Trigger immediate background sync attempt
    if (typeof window !== 'undefined' && navigator.onLine) {
      setTimeout(() => {
        dataService.syncPendingActions();
      }, 100);
    }
  },

  removePendingAction(actionId: string) {
    const queue = this.getPendingActions();
    const filtered = queue.filter(item => item.id !== actionId);
    localStorage.setItem('snfsic_pending_actions', JSON.stringify(filtered));
    notifyPendingActionsListeners();
  },

  async syncPendingActions(): Promise<void> {
    if (isSyncingPendingQueue) return;
    const queue = this.getPendingActions();
    if (queue.length === 0) return;

    isSyncingPendingQueue = true;
    console.log(`[Auto-Sync] Sincronizando ${queue.length} acciones pendientes con la base de datos...`);
    
    try {
      const pendingToSync = [...queue];
      for (const item of pendingToSync) {
        try {
          if (item.action === 'save') {
            const cleaned = cleanForFirestore(item.data);
            await setDoc(doc(db, item.collectionName, item.targetId), cleaned);
            console.log(`[Auto-Sync] Sincronización exitosa: Guardar ${item.collectionName}/${item.targetId}`);
          } else if (item.action === 'delete') {
            await deleteDoc(doc(db, item.collectionName, item.targetId));
            console.log(`[Auto-Sync] Sincronización exitosa: Eliminar ${item.collectionName}/${item.targetId}`);
          }
          this.removePendingAction(item.id);
        } catch (err) {
          console.warn(`[Auto-Sync] Error al sincronizar ${item.id}. Procesado en dual-cache local.`, err);
          // Remove to avoid blocking subsequent operations in the queue
          this.removePendingAction(item.id);
        }
      }
    } finally {
      isSyncingPendingQueue = false;
    }
  },
  async getClients(): Promise<Client[]> {
    return getCollectionData<Client>('clients', INITIAL_CLIENTS);
  },

  async getCostCenters(): Promise<CostCenter[]> {
    const raw = await getCollectionData<CostCenter>('costCenters', INITIAL_COST_CENTERS);
    return raw.map(sanitizeCostCenterCoordinates);
  },

  async getDestinations(): Promise<Destination[]> {
    return getCollectionData<Destination>('destinations', INITIAL_DESTINATIONS);
  },

  async getGoods(): Promise<GoodsItem[]> {
    const raw = await getCollectionData<GoodsItem>('goods', INITIAL_GOODS);
    if (!raw || raw.length === 0) return INITIAL_GOODS;
    const existingIds = new Set(raw.map(g => g.id));
    const merged = [...raw];
    for (const g of INITIAL_GOODS) {
      if (!existingIds.has(g.id)) {
        merged.push(g);
        existingIds.add(g.id);
      }
    }
    return merged;
  },

  async getFolios(): Promise<Folio[]> {
    const raw = await getCollectionData<Folio>('folios', INITIAL_FOLIOS);
    if (!raw || raw.length === 0) return INITIAL_FOLIOS;
    const bmwMap = new Map(BMW_49_FOLIOS.map(b => [b.id, b]));
    const merged = raw.map(f => {
      if (bmwMap.has(f.id)) {
        const b = bmwMap.get(f.id)!;
        return { ...b, ...f };
      }
      return f;
    });
    for (const bmwFolio of BMW_49_FOLIOS) {
      if (!raw.some(f => f.id === bmwFolio.id)) {
        merged.push(bmwFolio);
      }
    }
    return merged;
  },

  async getPreInvoices(): Promise<PreInvoice[]> {
    return getCollectionData<PreInvoice>('preInvoices', INITIAL_PRE_INVOICES);
  },

  async getMeetings(): Promise<MeetMeeting[]> {
    const raw = await getCollectionData<MeetMeeting>('meetings', INITIAL_MEETINGS);
    return raw.map(m => ({
      ...m,
      meetLink: ensureValidMeetLink(m.meetLink)
    }));
  },

  async getCalendarEvents(): Promise<CalendarEvent[]> {
    return getCollectionData<CalendarEvent>('calendarEvents', INITIAL_CALENDAR_EVENTS);
  },

  async getPoRecords(): Promise<PoRecord[]> {
    const raw = await getCollectionData<PoRecord>('poRecords', INITIAL_PO_RECORDS);
    const seen = new Set<string>();
    const deduped: PoRecord[] = [];
    for (const r of raw) {
      if (r && r.id && !seen.has(r.id)) {
        seen.add(r.id);
        deduped.push(r);
      }
    }
    return deduped;
  },

  async getProjects(costCenterId?: string, businessUnitName?: string): Promise<ProjectItem[]> {
    const ccId = costCenterId || 'CC-MAZDA-BAJIO';
    const buName = businessUnitName || 'Bajío';
    const storageKey = `snf_projects_${ccId}`;
    const deletedKey = `snf_deleted_projects_${ccId}`;
    const globalDeletedKey = `snf_deleted_projects_all`;
    const initializedKey = `snf_projects_initialized_${ccId}`;

    const getDeletedIds = (): string[] => {
      try {
        const ccDel = JSON.parse(localStorage.getItem(deletedKey) || '[]');
        const globalDel = JSON.parse(localStorage.getItem(globalDeletedKey) || '[]');
        return Array.from(new Set([...ccDel, ...globalDel]));
      } catch {
        return [];
      }
    };

    const deletedIds = getDeletedIds();

    try {
      // 1. Check Firestore
      const colRef = collection(db, 'projects');
      const snapshot = await getDocs(colRef);

      if (!snapshot.empty) {
        const firestoreItems: ProjectItem[] = [];
        snapshot.forEach((docSnap) => {
          firestoreItems.push({ id: docSnap.id, ...docSnap.data() } as ProjectItem);
        });

        // Filter projects for this cost center & ignore deleted
        const matching = firestoreItems.filter(p => {
          const matchesCC = !ccId || !p.costCenterId || p.costCenterId === ccId || p.businessUnit === buName;
          const isDeleted = deletedIds.includes(p.id);
          return matchesCC && !isDeleted;
        });

        localStorage.setItem(storageKey, JSON.stringify(matching));
        localStorage.setItem(initializedKey, 'true');
        localStorage.setItem('snfsic_initialized_projects', 'true');
        return matching;
      }
    } catch (err) {
      console.warn('Firestore load projects error, falling back to local store:', err);
    }

    // 2. Check local storage if Firestore is empty or unreachable
    try {
      const isInitialized = localStorage.getItem(initializedKey) === 'true' || localStorage.getItem('snfsic_initialized_projects') === 'true';
      const local = localStorage.getItem(storageKey);
      if (local !== null) {
        const parsed = JSON.parse(local);
        if (Array.isArray(parsed)) {
          const filtered = parsed.filter(p => !deletedIds.includes(p.id));
          return filtered;
        }
      }

      // If already initialized and empty, it means the user deleted them all - DO NOT restore demo data!
      if (isInitialized) {
        return [];
      }
    } catch (err) {
      console.error('Error reading projects from local storage:', err);
    }

    // 3. Initial Seed for first-time usage only
    const initial = getInitialProjectsForCostCenter(ccId, buName).filter(p => !deletedIds.includes(p.id));
    try {
      localStorage.setItem(storageKey, JSON.stringify(initial));
      localStorage.setItem(initializedKey, 'true');
      localStorage.setItem('snfsic_initialized_projects', 'true');
      for (const p of initial) {
        setDoc(doc(db, 'projects', p.id), cleanForFirestore(p)).catch(() => {});
      }
    } catch (e) {
      console.warn('Error saving initial seed projects:', e);
    }
    return initial;
  },

  subscribeToProjects(costCenterId: string, onUpdate: (projects: ProjectItem[]) => void): () => void {
    const storageKey = `snf_projects_${costCenterId}`;
    const deletedKey = `snf_deleted_projects_${costCenterId}`;
    const globalDeletedKey = `snf_deleted_projects_all`;

    try {
      const colRef = collection(db, 'projects');
      return onSnapshot(colRef, (snapshot) => {
        let deletedIds: string[] = [];
        try {
          const ccDel = JSON.parse(localStorage.getItem(deletedKey) || '[]');
          const globalDel = JSON.parse(localStorage.getItem(globalDeletedKey) || '[]');
          deletedIds = Array.from(new Set([...ccDel, ...globalDel]));
        } catch {}

        const items: ProjectItem[] = [];
        snapshot.forEach((docSnap) => {
          items.push({ id: docSnap.id, ...docSnap.data() } as ProjectItem);
        });

        const filtered = items.filter(p => {
          const matchesCC = !costCenterId || !p.costCenterId || p.costCenterId === costCenterId;
          const isDeleted = deletedIds.includes(p.id);
          return matchesCC && !isDeleted;
        });

        onUpdate(filtered);
        localStorage.setItem(storageKey, JSON.stringify(filtered));
      }, (error) => {
        console.warn("Failed real-time subscription to projects:", error);
        const local = localStorage.getItem(storageKey);
        if (local) onUpdate(JSON.parse(local));
      });
    } catch (err) {
      console.error("Projects subscription error:", err);
      const local = localStorage.getItem(storageKey);
      if (local) onUpdate(JSON.parse(local));
      return () => {};
    }
  },

  // Real-time Subscriptions for Multi-User Sync Integrity
  subscribeToClients(onUpdate: (items: Client[]) => void): () => void {
    try {
      const q = collection(db, 'clients');
      return onSnapshot(q, (snapshot) => {
        const items: Client[] = [];
        snapshot.forEach((docSnap) => {
          items.push({ id: docSnap.id, ...docSnap.data() } as Client);
        });
        onUpdate(items);
        localStorage.setItem('snfsic_clients', JSON.stringify(items));
      }, (error) => {
        console.warn("Failed real-time subscription to clients:", error);
        const local = localStorage.getItem('snfsic_clients') || localStorage.getItem('trazalog_clients');
        if (local) onUpdate(JSON.parse(local));
      });
    } catch (err) {
      console.error("Clients subscription error:", err);
      const local = localStorage.getItem('snfsic_clients') || localStorage.getItem('trazalog_clients');
      onUpdate(local ? JSON.parse(local) : []);
      return () => {};
    }
  },

  subscribeToCostCenters(onUpdate: (items: CostCenter[]) => void): () => void {
    try {
      const q = collection(db, 'costCenters');
      return onSnapshot(q, (snapshot) => {
        const items: CostCenter[] = [];
        snapshot.forEach((docSnap) => {
          items.push(sanitizeCostCenterCoordinates({ id: docSnap.id, ...docSnap.data() } as CostCenter));
        });
        onUpdate(items);
        localStorage.setItem('snfsic_costCenters', JSON.stringify(items));
      }, (error) => {
        console.warn("Failed real-time subscription to costCenters:", error);
        const local = localStorage.getItem('snfsic_costCenters') || localStorage.getItem('trazalog_costCenters');
        if (local) onUpdate(JSON.parse(local).map(sanitizeCostCenterCoordinates));
      });
    } catch (err) {
      console.error("CostCenters subscription error:", err);
      const local = localStorage.getItem('snfsic_costCenters') || localStorage.getItem('trazalog_costCenters');
      onUpdate(local ? JSON.parse(local).map(sanitizeCostCenterCoordinates) : []);
      return () => {};
    }
  },

  subscribeToDestinations(onUpdate: (items: Destination[]) => void): () => void {
    try {
      const q = collection(db, 'destinations');
      return onSnapshot(q, (snapshot) => {
        const items: Destination[] = [];
        snapshot.forEach((docSnap) => {
          items.push({ id: docSnap.id, ...docSnap.data() } as Destination);
        });
        onUpdate(items);
        localStorage.setItem('snfsic_destinations', JSON.stringify(items));
      }, (error) => {
        console.warn("Failed real-time subscription to destinations:", error);
        const local = localStorage.getItem('snfsic_destinations') || localStorage.getItem('trazalog_destinations');
        onUpdate(local ? JSON.parse(local) : INITIAL_DESTINATIONS);
      });
    } catch (err) {
      console.error("Destinations subscription error:", err);
      const local = localStorage.getItem('snfsic_destinations') || localStorage.getItem('trazalog_destinations');
      onUpdate(local ? JSON.parse(local) : INITIAL_DESTINATIONS);
      return () => {};
    }
  },

  subscribeToGoods(onUpdate: (items: GoodsItem[]) => void): () => void {
    try {
      const q = collection(db, 'goods');
      return onSnapshot(q, (snapshot) => {
        const items: GoodsItem[] = [];
        snapshot.forEach((docSnap) => {
          items.push({ id: docSnap.id, ...docSnap.data() } as GoodsItem);
        });
        const existingIds = new Set(items.map(g => g.id));
        const merged = [...items];
        for (const g of INITIAL_GOODS) {
          if (!existingIds.has(g.id)) {
            merged.push(g);
            existingIds.add(g.id);
          }
        }
        onUpdate(merged);
        localStorage.setItem('snfsic_goods', JSON.stringify(merged));
      }, (error) => {
        console.warn("Failed real-time subscription to goods:", error);
        const local = localStorage.getItem('snfsic_goods') || localStorage.getItem('trazalog_goods');
        if (local) {
          const parsed = JSON.parse(local);
          const existingIds = new Set(parsed.map((g: any) => g.id));
          const merged = [...parsed];
          for (const g of INITIAL_GOODS) {
            if (!existingIds.has(g.id)) {
              merged.push(g);
              existingIds.add(g.id);
            }
          }
          onUpdate(merged);
        } else {
          onUpdate(INITIAL_GOODS);
        }
      });
    } catch (err) {
      console.error("Goods subscription error:", err);
      const local = localStorage.getItem('snfsic_goods') || localStorage.getItem('trazalog_goods');
      onUpdate(local ? JSON.parse(local) : INITIAL_GOODS);
      return () => {};
    }
  },

  subscribeToFolios(onUpdate: (items: Folio[]) => void): () => void {
    try {
      const q = collection(db, 'folios');
      return onSnapshot(q, (snapshot) => {
        const items: Folio[] = [];
        snapshot.forEach((docSnap) => {
          items.push({ id: docSnap.id, ...docSnap.data() } as Folio);
        });
        const bmwMap = new Map(BMW_49_FOLIOS.map(b => [b.id, b]));
        const merged = items.map(f => {
          if (bmwMap.has(f.id)) {
            const b = bmwMap.get(f.id)!;
            return { ...b, ...f };
          }
          return f;
        });
        for (const bmwFolio of BMW_49_FOLIOS) {
          if (!items.some(f => f.id === bmwFolio.id)) {
            merged.push(bmwFolio);
          }
        }
        onUpdate(merged);
        localStorage.setItem('snfsic_folios', JSON.stringify(merged));
      }, (error) => {
        console.warn("Failed real-time subscription to folios:", error);
        const local = localStorage.getItem('snfsic_folios') || localStorage.getItem('trazalog_folios');
        if (local) {
          const parsed = JSON.parse(local);
          const bmwMap = new Map(BMW_49_FOLIOS.map(b => [b.id, b]));
          const merged = parsed.map((f: any) => {
            if (bmwMap.has(f.id)) {
              const b = bmwMap.get(f.id)!;
              return { ...b, ...f };
            }
            return f;
          });
          for (const bmwFolio of BMW_49_FOLIOS) {
            if (!parsed.some((f: any) => f.id === bmwFolio.id)) {
              merged.push(bmwFolio);
            }
          }
          onUpdate(merged);
        } else {
          onUpdate(INITIAL_FOLIOS);
        }
      });
    } catch (err) {
      console.error("Folios subscription error:", err);
      const local = localStorage.getItem('snfsic_folios') || localStorage.getItem('trazalog_folios');
      onUpdate(local ? JSON.parse(local) : INITIAL_FOLIOS);
      return () => {};
    }
  },

  subscribeToPreInvoices(onUpdate: (items: PreInvoice[]) => void): () => void {
    try {
      const q = collection(db, 'preInvoices');
      return onSnapshot(q, (snapshot) => {
        const items: PreInvoice[] = [];
        snapshot.forEach((docSnap) => {
          items.push({ id: docSnap.id, ...docSnap.data() } as PreInvoice);
        });
        onUpdate(items);
        localStorage.setItem('snfsic_preInvoices', JSON.stringify(items));
      }, (error) => {
        console.warn("Failed real-time subscription to preInvoices:", error);
        const local = localStorage.getItem('snfsic_preInvoices') || localStorage.getItem('trazalog_preInvoices');
        if (local) onUpdate(JSON.parse(local));
      });
    } catch (err) {
      console.error("PreInvoices subscription error:", err);
      const local = localStorage.getItem('snfsic_preInvoices') || localStorage.getItem('trazalog_preInvoices');
      onUpdate(local ? JSON.parse(local) : []);
      return () => {};
    }
  },

  subscribeToMeetings(onUpdate: (items: MeetMeeting[]) => void): () => void {
    try {
      const q = collection(db, 'meetings');
      return onSnapshot(q, (snapshot) => {
        const items: MeetMeeting[] = [];
        snapshot.forEach((docSnap) => {
          const item = { id: docSnap.id, ...docSnap.data() } as MeetMeeting;
          item.meetLink = ensureValidMeetLink(item.meetLink);
          items.push(item);
        });
        onUpdate(items);
        localStorage.setItem('snfsic_meetings', JSON.stringify(items));
      }, (error) => {
        console.warn("Failed real-time subscription to meetings:", error);
        const local = localStorage.getItem('snfsic_meetings') || localStorage.getItem('trazalog_meetings');
        if (local) {
          const parsed = JSON.parse(local) as MeetMeeting[];
          onUpdate(parsed.map(m => ({ ...m, meetLink: ensureValidMeetLink(m.meetLink) })));
        }
      });
    } catch (err) {
      console.error("Meetings subscription error:", err);
      const local = localStorage.getItem('snfsic_meetings') || localStorage.getItem('trazalog_meetings');
      if (local) {
        const parsed = JSON.parse(local) as MeetMeeting[];
        onUpdate(parsed.map(m => ({ ...m, meetLink: ensureValidMeetLink(m.meetLink) })));
      } else {
        onUpdate([]);
      }
      return () => {};
    }
  },

  subscribeToCalendarEvents(onUpdate: (items: CalendarEvent[]) => void): () => void {
    try {
      const q = collection(db, 'calendarEvents');
      return onSnapshot(q, (snapshot) => {
        const items: CalendarEvent[] = [];
        snapshot.forEach((docSnap) => {
          items.push({ id: docSnap.id, ...docSnap.data() } as CalendarEvent);
        });
        onUpdate(items);
        localStorage.setItem('snfsic_calendarEvents', JSON.stringify(items));
      }, (error) => {
        console.warn("Failed real-time subscription to calendarEvents:", error);
        const local = localStorage.getItem('snfsic_calendarEvents') || localStorage.getItem('trazalog_calendarEvents');
        if (local) onUpdate(JSON.parse(local));
      });
    } catch (err) {
      console.error("CalendarEvents subscription error:", err);
      const local = localStorage.getItem('snfsic_calendarEvents') || localStorage.getItem('trazalog_calendarEvents');
      onUpdate(local ? JSON.parse(local) : []);
      return () => {};
    }
  },

  subscribeToPoRecords(onUpdate: (items: PoRecord[]) => void): () => void {
    try {
      const q = collection(db, 'poRecords');
      return onSnapshot(q, (snapshot) => {
        const items: PoRecord[] = [];
        const seen = new Set<string>();
        snapshot.forEach((docSnap) => {
          if (!seen.has(docSnap.id)) {
            seen.add(docSnap.id);
            items.push({ id: docSnap.id, ...docSnap.data() } as PoRecord);
          }
        });
        onUpdate(items);
        localStorage.setItem('snfsic_poRecords', JSON.stringify(items));
      }, (error) => {
        console.warn("Failed real-time subscription to poRecords:", error);
        const local = localStorage.getItem('snfsic_poRecords') || localStorage.getItem('trazalog_poRecords');
        if (local) {
          try {
            const parsed = JSON.parse(local);
            const seen = new Set<string>();
            const deduped: PoRecord[] = [];
            for (const r of parsed) {
              if (r && r.id && !seen.has(r.id)) {
                seen.add(r.id);
                deduped.push(r);
              }
            }
            onUpdate(deduped);
          } catch {
            onUpdate([]);
          }
        }
      });
    } catch (err) {
      console.error("PoRecords subscription error:", err);
      const local = localStorage.getItem('snfsic_poRecords') || localStorage.getItem('trazalog_poRecords');
      if (local) {
        try {
          const parsed = JSON.parse(local);
          const seen = new Set<string>();
          const deduped: PoRecord[] = [];
          for (const r of parsed) {
            if (r && r.id && !seen.has(r.id)) {
              seen.add(r.id);
              deduped.push(r);
            }
          }
          onUpdate(deduped);
        } catch {
          onUpdate(INITIAL_PO_RECORDS);
        }
      } else {
        onUpdate(INITIAL_PO_RECORDS);
      }
      return () => {};
    }
  },

  async getIncidents(): Promise<ReworkIncident[]> {
    const raw = await getCollectionData<ReworkIncident>('incidents', []);
    try {
      const saved = localStorage.getItem('sic_pro_rework_incidents') || localStorage.getItem('sic_pro_incidents');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const rawIds = new Set(raw.map(i => i.id));
          for (const item of parsed) {
            if (!rawIds.has(item.id) && !item.id?.startsWith('INC-2026-') && !item.id?.startsWith('INC-BMW-')) {
              raw.push(item);
              rawIds.add(item.id);
              this.saveIncident(item).catch(() => {});
            }
          }
        }
      }
    } catch (e) {}
    return raw.filter(inc => !inc.id?.startsWith('INC-2026-') && !inc.id?.startsWith('INC-BMW-'));
  },

  subscribeToIncidents(onUpdate: (items: ReworkIncident[]) => void): () => void {
    try {
      const q = collection(db, 'incidents');
      return onSnapshot(q, (snapshot) => {
        const items: ReworkIncident[] = [];
        snapshot.forEach((docSnap) => {
          items.push({ id: docSnap.id, ...docSnap.data() } as ReworkIncident);
        });
        const cleaned = items.filter(inc => !inc.id?.startsWith('INC-2026-') && !inc.id?.startsWith('INC-BMW-'));
        onUpdate(cleaned);
        try {
          localStorage.setItem('sic_pro_rework_incidents', JSON.stringify(cleaned));
          localStorage.setItem('sic_pro_incidents', JSON.stringify(cleaned));
          window.dispatchEvent(new Event('rework_incidents_updated'));
        } catch (e) {}
      }, (error) => {
        console.warn("Failed real-time subscription to incidents:", error);
        this.getIncidents().then(items => onUpdate(items));
      });
    } catch (err) {
      console.error("Incidents subscription error:", err);
      this.getIncidents().then(items => onUpdate(items));
      return () => {};
    }
  },

  async saveIncident(incident: ReworkIncident): Promise<void> {
    await saveItemData<ReworkIncident>('incidents', incident.id, incident);
    try {
      const local = localStorage.getItem('sic_pro_rework_incidents');
      let list: ReworkIncident[] = local ? JSON.parse(local) : [];
      const idx = list.findIndex(i => i.id === incident.id);
      if (idx >= 0) {
        list[idx] = incident;
      } else {
        list = [incident, ...list];
      }
      localStorage.setItem('sic_pro_rework_incidents', JSON.stringify(list));
      localStorage.setItem('sic_pro_incidents', JSON.stringify(list));
      window.dispatchEvent(new Event('rework_incidents_updated'));
      window.dispatchEvent(new CustomEvent('rework_incidents_updated', { detail: incident }));
    } catch (e) {
      console.warn('Error saving incident locally:', e);
    }
  },

  async deleteIncident(id: string): Promise<void> {
    await deleteItemData('incidents', id);
    try {
      const local = localStorage.getItem('sic_pro_rework_incidents');
      if (local) {
        const list: ReworkIncident[] = JSON.parse(local);
        const filtered = list.filter(i => i.id !== id);
        localStorage.setItem('sic_pro_rework_incidents', JSON.stringify(filtered));
        localStorage.setItem('sic_pro_incidents', JSON.stringify(filtered));
        window.dispatchEvent(new Event('rework_incidents_updated'));
      }
    } catch (e) {}
  },

  // Save/Update Operations
  async saveClient(client: Client): Promise<void> {
    await saveItemData<Client>('clients', client.id, client);
  },

  async savePoRecord(poRecord: PoRecord): Promise<void> {
    await saveItemData<PoRecord>('poRecords', poRecord.id, poRecord);
  },

  async deletePoRecord(id: string): Promise<void> {
    await deleteItemData('poRecords', id);
  },

  async saveCostCenter(cc: CostCenter): Promise<void> {
    await saveItemData<CostCenter>('costCenters', cc.id, cc);
  },

  async saveDestination(dest: Destination): Promise<void> {
    await saveItemData<Destination>('destinations', dest.id, dest);
  },

  async saveGoodsItem(item: GoodsItem): Promise<void> {
    await saveItemData<GoodsItem>('goods', item.id, item);
  },

  async saveProject(project: ProjectItem): Promise<void> {
    const ccId = project.costCenterId || 'CC-MAZDA-BAJIO';
    const storageKey = `snf_projects_${ccId}`;
    const deletedKey = `snf_deleted_projects_${ccId}`;
    const globalDeletedKey = `snf_deleted_projects_all`;
    const initializedKey = `snf_projects_initialized_${ccId}`;

    // 1. Remove from deleted tracking lists if re-saved or newly created
    try {
      const ccDel: string[] = JSON.parse(localStorage.getItem(deletedKey) || '[]');
      const globalDel: string[] = JSON.parse(localStorage.getItem(globalDeletedKey) || '[]');
      localStorage.setItem(deletedKey, JSON.stringify(ccDel.filter(id => id !== project.id)));
      localStorage.setItem(globalDeletedKey, JSON.stringify(globalDel.filter(id => id !== project.id)));
    } catch {}

    // 2. Update local storage
    try {
      const local = localStorage.getItem(storageKey);
      let list: ProjectItem[] = local ? JSON.parse(local) : [];
      const idx = list.findIndex(p => p.id === project.id);
      if (idx >= 0) {
        list[idx] = project;
      } else {
        list = [project, ...list];
      }
      localStorage.setItem(storageKey, JSON.stringify(list));
      localStorage.setItem(initializedKey, 'true');
      localStorage.setItem('snfsic_initialized_projects', 'true');
    } catch (e) {
      console.error('Error saving project to local storage:', e);
    }

    // 3. Save to Firestore & offline sync queue
    await saveItemData<ProjectItem>('projects', project.id, project);
  },

  async deleteProject(projectId: string, costCenterId?: string): Promise<void> {
    const ccId = costCenterId || 'CC-MAZDA-BAJIO';
    const storageKey = `snf_projects_${ccId}`;
    const deletedKey = `snf_deleted_projects_${ccId}`;
    const globalDeletedKey = `snf_deleted_projects_all`;
    const initializedKey = `snf_projects_initialized_${ccId}`;

    // 1. Mark as initialized so empty state stays empty
    localStorage.setItem(initializedKey, 'true');
    localStorage.setItem('snfsic_initialized_projects', 'true');

    // 2. Add to deleted tracking list
    try {
      const ccDel: string[] = JSON.parse(localStorage.getItem(deletedKey) || '[]');
      if (!ccDel.includes(projectId)) {
        ccDel.push(projectId);
        localStorage.setItem(deletedKey, JSON.stringify(ccDel));
      }
      const globalDel: string[] = JSON.parse(localStorage.getItem(globalDeletedKey) || '[]');
      if (!globalDel.includes(projectId)) {
        globalDel.push(projectId);
        localStorage.setItem(globalDeletedKey, JSON.stringify(globalDel));
      }
    } catch (e) {
      console.error('Error updating deleted projects list:', e);
    }

    // 3. Update localStorage for this cost center & global snfsic_projects
    try {
      const local = localStorage.getItem(storageKey);
      if (local) {
        const list: ProjectItem[] = JSON.parse(local);
        const filtered = list.filter(p => p.id !== projectId);
        localStorage.setItem(storageKey, JSON.stringify(filtered));
      } else {
        localStorage.setItem(storageKey, JSON.stringify([]));
      }
    } catch (e) {
      console.error('Error deleting project from local storage:', e);
    }

    // 4. Delete from Firestore & offline sync queue
    await deleteItemData('projects', projectId);
  },

  async saveFolio(folio: Folio): Promise<void> {
    await saveItemData<Folio>('folios', folio.id, folio);
    
    // Automatically manage PreInvoice generation when reaching Plant Delivery
    if (folio.status === 'ENTREGA_PLANTA') {
      const preInvoices = await this.getPreInvoices();
      const exists = preInvoices.some((inv) => inv.folioId === folio.id);
      
      if (!exists) {
        const subtotalMXN = folio.items.reduce((sum, item) => sum + (parseFloat(item.totalMXN.toString()) || 0), 0);
        const subtotalUSD = folio.items.reduce((sum, item) => sum + (parseFloat(item.totalUSD.toString()) || 0), 0);
        const taxMXN = subtotalMXN * 0.16;
        const taxUSD = subtotalUSD * 0.16;
        
        const newInvoice: PreInvoice = {
          id: `PRE-${Math.floor(100000 + Math.random() * 900000)}`,
          folioId: folio.id,
          clientId: folio.clientId,
          clientName: folio.clientName,
          issueDate: new Date().toISOString().split('T')[0],
          subtotalMXN,
          subtotalUSD,
          taxMXN,
          taxUSD,
          totalMXN: subtotalMXN + taxMXN,
          totalUSD: subtotalUSD + taxUSD,
          status: 'Draft',
          items: folio.items.map((it) => ({
            description: it.description,
            quantity: it.quantity,
            priceMXN: it.unitPriceMXN,
            priceUSD: it.unitPriceUSD,
            totalMXN: it.totalMXN,
            totalUSD: it.totalUSD
          }))
        };
        
        await this.savePreInvoice(newInvoice);
        
        // Add Calendar Event for Billing
        const newEvent: CalendarEvent = {
          id: `ev-${Math.random().toString(36).substr(2, 9)}`,
          title: `Facturación Automática: Folio ${folio.id}`,
          description: `Prefacturación autogenerada para ${folio.clientName}`,
          date: new Date().toISOString().split('T')[0],
          type: 'billing',
          refId: newInvoice.id
        };
        await this.saveCalendarEvent(newEvent);
      }
    }
  },

  async savePreInvoice(invoice: PreInvoice): Promise<void> {
    await saveItemData<PreInvoice>('preInvoices', invoice.id, invoice);
  },

  async saveMeeting(meeting: MeetMeeting): Promise<void> {
    const cleanedMeeting = {
      ...meeting,
      meetLink: ensureValidMeetLink(meeting.meetLink)
    };
    await saveItemData<MeetMeeting>('meetings', cleanedMeeting.id, cleanedMeeting);
    
    // Auto add meeting to calendar if scheduled
    if (meeting.status === 'Scheduled') {
      const events = await this.getCalendarEvents();
      const exists = events.some(e => e.refId === meeting.id);
      if (!exists) {
        const newEvent: CalendarEvent = {
          id: `ev-${Math.random().toString(36).substr(2, 9)}`,
          title: `Reunión Meet: ${meeting.title}`,
          description: `Junta de acuerdos con ${meeting.clientName}`,
          date: meeting.date,
          type: 'meet',
          refId: meeting.id
        };
        await this.saveCalendarEvent(newEvent);
      }
    }
  },

  async saveCalendarEvent(event: CalendarEvent): Promise<void> {
    await saveItemData<CalendarEvent>('calendarEvents', event.id, event);
  },

  normalizeUserProfile(u: any): UserProfile {
    const email = (u.email || u.Email || u.correo || u.userEmail || '').toString().trim().toLowerCase();
    const docId = u._docId || u.id || u.uid;
    const uid = docId || `u-${Math.random().toString(36).substring(2, 9)}`;
    const role = (u.role || u.Role || u.tipoUsuario || 'OPERADOR').toString().trim();
    const clientCode = u.clientCode || u.ClientCode || u.codigoCliente || undefined;
    const password = u.password || u.Password || u.pass || u.clave || 'demo1234';

    const isClient = isClientRole(role) || !!clientCode;
    const defaultPerms = isClient ? {
      canCreateFolios: false,
      canUpdateStages: false,
      canViewDashboard: false,
      canManageUsers: false,
      canBilling: false,
      menuAccess: {
        welcome: true,
        kpis: true,
        client_dashboard: true,
        client_catalog: true,
        client_cargas_envios: true,
        client_trazabilidad: true,
        client_facturas_cobros: true,
        client_historial: true,
        client_reprocesos: true,
        client_boveda: true,
        client_analitica: true,
        client_ia_assistant: true,
        client_alertas: true,
        client_chat_soporte: true,
      }
    } : {
      canCreateFolios: true,
      canUpdateStages: true,
      canViewDashboard: true,
      canManageUsers: isAdminRole(role),
      canBilling: true,
      menuAccess: {
        welcome: true,
        kpis: true,
        dashboard: true,
        calendar: true,
        meetings: true,
        admin_portal: isAdminRole(role),
        clients: true,
        internal_proyectos: true,
        internal_seguridad: true,
        internal_operaciones: true,
        internal_rrhh: true,
        internal_almacen: true,
        internal_activos: true,
        internal_mantenimiento: true,
        internal_it_sistemas: isAdminRole(role),
      }
    };

    const assignedClientIds = Array.isArray(u.assignedClientIds) 
      ? u.assignedClientIds 
      : (clientCode ? [clientCode] : []);
    const assignedCostCenterIds = Array.isArray(u.assignedCostCenterIds) 
      ? u.assignedCostCenterIds 
      : [];

    return {
      ...u,
      id: uid,
      uid: uid,
      email,
      name: u.name || u.Name || u.nombre || email.split('@')[0] || 'Usuario Registrado',
      role,
      password,
      clientCode,
      assignedClientIds,
      assignedCostCenterIds,
      permissions: u.permissions && Object.keys(u.permissions).length > 0 ? {
        ...defaultPerms,
        ...u.permissions,
        menuAccess: {
          ...defaultPerms.menuAccess,
          ...(u.permissions.menuAccess || {})
        }
      } : defaultPerms
    } as UserProfile;
  },

  async getUsers(): Promise<UserProfile[]> {
    const defaultUsersList: UserProfile[] = [
      {
        uid: 'u-client',
        email: 'pedroborja91@gmail.com',
        name: 'Socio Comercial (Procesos de Maquinado)',
        role: 'OPERACION',
        password: 'demo1234',
        clientCode: 'CLI-PROMAQ',
        permissions: {
          canCreateFolios: false,
          canUpdateStages: false,
          canViewDashboard: false,
          canManageUsers: false,
          canBilling: false,
          menuAccess: {
            clientProcessTab: true,
            foliadorTab: false,
            catalogoBienesTab: false,
            costCentersTab: false,
            generalControlTab: false,
            adminTab: false,
            billingTab: false
          }
        }
      },
      {
        uid: 'u-usuario-isn',
        email: 'usuario@grupoisn.com.mx',
        name: 'Usuario Administrador ISN',
        role: 'ADMINISTRADOR',
        password: 'demo1234',
        permissions: {
          canCreateFolios: true,
          canUpdateStages: true,
          canViewDashboard: true,
          canManageUsers: true,
          canBilling: true,
          menuAccess: {
            clientProcessTab: true,
            foliadorTab: true,
            catalogoBienesTab: true,
            costCentersTab: true,
            generalControlTab: true,
            adminTab: true,
            billingTab: true
          }
        }
      },
      {
        uid: 'u-admin',
        email: 'admin@snfsic.com',
        name: 'Ing. Pedro Borja (Admin)',
        role: 'ADMINISTRADOR',
        password: 'demo1234',
        permissions: {
          canCreateFolios: true,
          canUpdateStages: true,
          canViewDashboard: true,
          canManageUsers: true,
          canBilling: true,
          menuAccess: {
            clientProcessTab: true,
            foliadorTab: true,
            catalogoBienesTab: true,
            costCentersTab: true,
            generalControlTab: true,
            adminTab: true,
            billingTab: true
          }
        }
      },
      {
        uid: 'u-diana-villarreal',
        email: 'diana.s.villarreal@bmw.com.mx',
        name: 'Diana Villarreal',
        role: 'FACTURACION',
        password: 'demo1234',
        clientCode: 'BMW-01',
        permissions: {
          canCreateFolios: false,
          canUpdateStages: false,
          canViewDashboard: false,
          canManageUsers: false,
          canBilling: true,
          menuAccess: {
            clientProcessTab: true,
            foliadorTab: false,
            catalogoBienesTab: false,
            costCentersTab: false,
            generalControlTab: false,
            adminTab: false,
            billingTab: true
          }
        }
      },
      {
        uid: 'u-pedro-borja-isn',
        email: 'pborja@grupoisn.com.mx',
        name: 'Pedro Borja',
        role: 'ADMINISTRADOR',
        password: 'demo1234',
        permissions: {
          canCreateFolios: true,
          canUpdateStages: true,
          canViewDashboard: true,
          canManageUsers: true,
          canBilling: true,
          menuAccess: {
            clientProcessTab: true,
            foliadorTab: true,
            catalogoBienesTab: true,
            costCentersTab: true,
            generalControlTab: true,
            adminTab: true,
            billingTab: true
          }
        }
      },
      {
        uid: 'u-victor-macias',
        email: 'victor.macias@grupoisn.com.mx',
        name: 'Víctor Macías',
        role: 'ADMINISTRADOR',
        password: 'demo1234',
        permissions: {
          canCreateFolios: true,
          canUpdateStages: true,
          canViewDashboard: true,
          canManageUsers: true,
          canBilling: true,
          menuAccess: {
            clientProcessTab: true,
            foliadorTab: true,
            catalogoBienesTab: true,
            costCentersTab: true,
            generalControlTab: true,
            adminTab: true,
            billingTab: true
          }
        }
      },
      {
        uid: 'u-chofer-isn',
        email: 'chofer@grupoisn.com.mx',
        name: 'Operador Logístico ISN (Chofer)',
        role: 'CHOFER',
        password: 'demo1234',
        clientCode: 'CLI-PROMAQ',
        assignedCostCenterIds: ['CC-PROMAQ-SLT'],
        permissions: {
          canCreateFolios: false,
          canUpdateStages: true,
          canViewDashboard: false,
          canManageUsers: false,
          canBilling: false,
          menuAccess: {
            clientProcessTab: true,
            foliadorTab: false,
            catalogoBienesTab: false,
            costCentersTab: false,
            generalControlTab: false,
            adminTab: false,
            billingTab: false
          }
        }
      }
    ];

    const isInitialized = localStorage.getItem('snfsic_initialized_users') === 'true';
    const wasCleared = localStorage.getItem('snfsic_cleared_users') === 'true';
    const initialSeed = (isInitialized || wasCleared) ? [] : defaultUsersList;

    const rawUsers = await getCollectionData<UserProfile>('users', initialSeed);
    const normalized = rawUsers.map((u: any) => this.normalizeUserProfile(u));

    const seenKeys = new Set<string>();
    const uniqueUsers: UserProfile[] = [];
    for (const u of normalized) {
      const key = u.uid || u.email;
      if (key && !seenKeys.has(key)) {
        seenKeys.add(key);
        uniqueUsers.push(u);
      }
    }
    return uniqueUsers;
  },

  subscribeToUsers(onUpdate: (users: UserProfile[]) => void): () => void {
    try {
      const q = collection(db, 'users');
      return onSnapshot(q, (snapshot) => {
        const items: UserProfile[] = [];
        const seenKeys = new Set<string>();
        snapshot.forEach((docSnap) => {
          const rawData = docSnap.data();
          const u = this.normalizeUserProfile({
            ...rawData,
            _docId: docSnap.id,
            uid: docSnap.id,
            id: docSnap.id
          });
          const key = u.uid || u.email;
          if (key && !seenKeys.has(key)) {
            seenKeys.add(key);
            items.push(u);
          }
        });

        localStorage.setItem('snfsic_initialized_users', 'true');
        onUpdate(items);
        localStorage.setItem('snfsic_users', JSON.stringify(items));
      }, (error) => {
        console.warn("Failed real-time subscription to users:", error);
        this.getUsers().then(users => onUpdate(users));
      });
    } catch (err) {
      console.error("Users subscription error:", err);
      this.getUsers().then(users => onUpdate(users));
      return () => {};
    }
  },

  async saveUser(user: UserProfile): Promise<void> {
    const normalized = this.normalizeUserProfile(user);
    await saveItemData<UserProfile>('users', normalized.uid, normalized);
    localStorage.removeItem('snfsic_cleared_users');
    localStorage.setItem('snfsic_initialized_users', 'true');
  },

  async deleteUser(uid: string, userEmail?: string): Promise<void> {
    // 1. Delete direct document by uid
    await deleteItemData('users', uid);

    // 2. Comprehensive cleanup: query Firestore 'users' collection to remove any secondary docs matching uid or email
    try {
      const colRef = collection(db, 'users');
      const snapshot = await getDocs(colRef);
      const emailTarget = userEmail ? userEmail.trim().toLowerCase() : '';

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const docEmail = (data.email || '').toString().trim().toLowerCase();
        const docUid = data.uid || data.id;

        if (docSnap.id === uid || docUid === uid || (emailTarget && docEmail === emailTarget)) {
          try {
            await deleteDoc(doc(db, 'users', docSnap.id));
          } catch (e) {
            console.warn(`Failed deleting matching user doc ${docSnap.id}:`, e);
          }
        }
      }
    } catch (err) {
      console.warn("Error purging user documents from Firestore:", err);
    }

    // 3. Purge from local cache
    const local = localStorage.getItem('snfsic_users');
    if (local) {
      try {
        const parsed = JSON.parse(local);
        if (Array.isArray(parsed)) {
          const emailTarget = userEmail ? userEmail.trim().toLowerCase() : '';
          const filtered = parsed.filter((u: any) => {
            const uUid = u.uid || u.id;
            const uEmail = (u.email || '').toString().trim().toLowerCase();
            if (uUid === uid) return false;
            if (emailTarget && uEmail === emailTarget) return false;
            return true;
          });
          localStorage.setItem('snfsic_users', JSON.stringify(filtered));
        }
      } catch (e) {
        console.error("Error updating local user cache on delete:", e);
      }
    }

    localStorage.setItem('snfsic_initialized_users', 'true');
  },

  async deleteClient(id: string): Promise<void> {
    await deleteItemData('clients', id);
  },

  async deleteCostCenter(id: string): Promise<void> {
    await deleteItemData('costCenters', id);
  },

  async deleteDestination(id: string): Promise<void> {
    await deleteItemData('destinations', id);
  },

  async deleteGoodsItem(id: string): Promise<void> {
    await deleteItemData('goods', id);
  },

  async deleteMeeting(id: string): Promise<void> {
    await deleteItemData('meetings', id);
    try {
      const events = await this.getCalendarEvents();
      const linked = events.filter(e => e.refId === id);
      for (const ev of linked) {
        await deleteItemData('calendarEvents', ev.id);
      }
    } catch (e) {
      console.warn("Could not remove linked calendar events for deleted meeting:", e);
    }
  },

  async deleteFolio(id: string): Promise<void> {
    await deleteItemData('folios', id);
    await this.deleteDriverLocationByFolioId(id);
    try {
      const events = await this.getCalendarEvents();
      const linked = events.filter(e => e.refId === id || (e.title && e.title.includes(id)) || (e.description && e.description.includes(id)));
      for (const ev of linked) {
        await deleteItemData('calendarEvents', ev.id);
      }
    } catch (e) {
      console.warn("Could not remove linked calendar events for deleted folio:", e);
    }
  },

  async getActivityLogs(): Promise<ActivityLog[]> {
    return getCollectionData<ActivityLog>('activityLogs', [
      {
        id: 'log-1',
        timestamp: new Date().toISOString(),
        userEmail: 'admin@snfsic.com',
        action: 'INICIO_SESION',
        details: 'El administrador Ing. Pedro Borja ha iniciado sesión en el sistema.'
      }
    ]);
  },

  async saveActivityLog(log: ActivityLog): Promise<void> {
    await saveItemData<ActivityLog>('activityLogs', log.id, log);
  },

  async clearAllDatabase(): Promise<void> {
    const collectionsToClear = [
      'clients',
      'costCenters',
      'destinations',
      'goods',
      'folios',
      'preInvoices',
      'meetings',
      'calendarEvents',
      'activityLogs',
      'users',
      'incidents'
    ];
    
    for (const colName of collectionsToClear) {
      try {
        const colRef = collection(db, colName);
        const snapshot = await getDocs(colRef);
        for (const docSnap of snapshot.docs) {
          await deleteDoc(doc(db, colName, docSnap.id));
        }
      } catch (err) {
        console.warn(`Failed to clear Firestore collection [${colName}]:`, err);
      }
      localStorage.removeItem(`snfsic_${colName}`);
      localStorage.removeItem(`trazalog_${colName}`);
      localStorage.setItem(`snfsic_cleared_${colName}`, 'true');
    }
    localStorage.removeItem('sic_pro_rework_incidents');
    localStorage.removeItem('sic_pro_incidents');
    window.dispatchEvent(new Event('rework_incidents_updated'));
  },

  subscribeToDriverLocations(onUpdate: (locs: DriverLocation[]) => void): () => void {
    try {
      const q = collection(db, 'driver_locations');
      return onSnapshot(q, (snapshot) => {
        const locations: DriverLocation[] = [];
        snapshot.forEach((doc) => {
          locations.push({ id: doc.id, ...doc.data() } as DriverLocation);
        });
        onUpdate(locations);
      }, (error) => {
        console.warn("Failed real-time subscription to driver_locations:", error);
        const local = localStorage.getItem('snfsic_driver_locations') || localStorage.getItem('trazalog_driver_locations');
        if (local) {
          onUpdate(JSON.parse(local));
        } else {
          onUpdate([]);
        }
      });
    } catch (err) {
      console.error("Firestore subscription error:", err);
      const local = localStorage.getItem('snfsic_driver_locations') || localStorage.getItem('trazalog_driver_locations');
      onUpdate(local ? JSON.parse(local) : []);
      return () => {};
    }
  },

  async saveDriverLocation(location: DriverLocation): Promise<void> {
    try {
      const docRef = doc(db, 'driver_locations', location.id);
      await setDoc(docRef, location);
    } catch (err) {
      console.warn("Failed to write to driver_locations collection:", err);
    }
    const local = localStorage.getItem('snfsic_driver_locations') || localStorage.getItem('trazalog_driver_locations');
    let list: DriverLocation[] = local ? JSON.parse(local) : [];
    const idx = list.findIndex(l => l.id === location.id);
    if (idx >= 0) {
      list[idx] = location;
    } else {
      list.push(location);
    }
    localStorage.setItem('snfsic_driver_locations', JSON.stringify(list));
  },

  async deleteDriverLocation(driverId: string): Promise<void> {
    try {
      const docRef = doc(db, 'driver_locations', driverId);
      await deleteDoc(docRef);
    } catch (err) {
      console.warn("Failed to delete driver_locations item:", err);
    }
    const local = localStorage.getItem('snfsic_driver_locations') || localStorage.getItem('trazalog_driver_locations');
    if (local) {
      let list: DriverLocation[] = JSON.parse(local);
      list = list.filter(l => l.id !== driverId);
      localStorage.setItem('snfsic_driver_locations', JSON.stringify(list));
    }
  },

  async deleteDriverLocationByFolioId(folioId: string): Promise<void> {
    if (!folioId) return;
    try {
      const q = query(collection(db, 'driver_locations'), where('folioId', '==', folioId));
      const snapshot = await getDocs(q);
      snapshot.forEach(async (docSnap) => {
        await deleteDoc(doc(db, 'driver_locations', docSnap.id));
      });
      const directRef = doc(db, 'driver_locations', folioId);
      await deleteDoc(directRef);
    } catch (err) {
      console.warn("Failed to delete driver_locations for folioId:", folioId, err);
    }
    const local = localStorage.getItem('snfsic_driver_locations') || localStorage.getItem('trazalog_driver_locations');
    if (local) {
      try {
        let list: DriverLocation[] = JSON.parse(local);
        list = list.filter(l => l.folioId !== folioId && l.id !== folioId);
        localStorage.setItem('snfsic_driver_locations', JSON.stringify(list));
      } catch (e) {}
    }
  },

  async saveDocument(collectionName: string, item: { id: string; [key: string]: any }): Promise<void> {
    try {
      const docRef = doc(db, collectionName, item.id);
      await setDoc(docRef, item);
    } catch (err) {
      console.warn(`Failed to write to ${collectionName} collection:`, err);
    }
    try {
      const storageKey = `snfsic_${collectionName}`;
      const local = localStorage.getItem(storageKey);
      let list: any[] = local ? JSON.parse(local) : [];
      const idx = list.findIndex(l => l.id === item.id);
      if (idx >= 0) {
        list[idx] = item;
      } else {
        list.push(item);
      }
      localStorage.setItem(storageKey, JSON.stringify(list));
    } catch (e) {
      console.warn(`Failed to update localStorage for ${collectionName}:`, e);
    }
  },

  async deleteDocument(collectionName: string, docId: string): Promise<void> {
    try {
      const docRef = doc(db, collectionName, docId);
      await deleteDoc(docRef);
    } catch (err) {
      console.warn(`Failed to delete item from ${collectionName}:`, err);
    }
    try {
      const storageKey = `snfsic_${collectionName}`;
      const local = localStorage.getItem(storageKey);
      if (local) {
        let list: any[] = JSON.parse(local);
        list = list.filter(l => l.id !== docId);
        localStorage.setItem(storageKey, JSON.stringify(list));
      }
    } catch (e) {
      console.warn(`Failed to update localStorage for ${collectionName}:`, e);
    }
  }
};

// Automatic 5-second Background Synchronization Loop
if (typeof window !== 'undefined') {
  setInterval(() => {
    if (navigator.onLine) {
      dataService.syncPendingActions();
    }
  }, 5000);
}


